// ProfileScreen.js — Full rebuild
// Fixes: image display (local+remote+b64), base64 persistence, API save/upload
// New: 9 tabs, Projects, Languages, Awards, Volunteer, skill bars, profile preview,
//      AI About generator, share, privacy settings, auto-save, all PROF_KEYS sync
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput,
  Image, Alert, SafeAreaView, StatusBar, Modal, RefreshControl,
  Dimensions, ActivityIndicator, Platform, Share, Switch,
  KeyboardAvoidingView, Animated,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFocusEffect } from '@react-navigation/native';

import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

let Ionicons, MaterialCommunityIcons;
try {
  Ionicons = require('@expo/vector-icons/Ionicons').default;
  MaterialCommunityIcons = require('@expo/vector-icons/MaterialCommunityIcons').default;
} catch {
  const FB = ({ size = 16, style }) => (
    <View style={[{ width: size, height: size, borderRadius: size / 2, backgroundColor: '#94a3b8' }, style]} />
  );
  Ionicons = MaterialCommunityIcons = FB;
}

let ImagePicker;
try { ImagePicker = require('expo-image-picker'); } catch { ImagePicker = null; }

const { width, height } = Dimensions.get('window');

// ─── API ─────────────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const apiFetch = async (endpoint, opts = {}) => {
  try {
    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('userToken');
    const res = await fetch(`${API_BASE}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json', 'x-api-key': API_KEY,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...opts,
    });
    if (!res.ok) return { error: true, status: res.status };
    return res.json();
  } catch (e) { return { error: true, message: e.message }; }
};

// ─── Shared keys — identical across Login/Signup/Dashboard/Home ──────────────
const PROF_KEYS = { PROFILE: 'prof_user_profile', DASH: 'prof_dashboard_data' };

// ─── Constants ────────────────────────────────────────────────────────────────
const SKILL_LEVELS   = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
const LANG_LEVELS    = ['Basic', 'Conversational', 'Professional', 'Fluent', 'Native'];
const AVAILABILITIES = ['Available', 'Open to Offers', 'Not Looking', 'Freelance Only'];
const NOTICE_PERIODS = ['Immediate', '1 week', '2 weeks', '1 month', '3 months'];
const WORK_LOCS      = ['On-site', 'Hybrid', 'Remote', 'Any'];
const JOB_TYPES_LIST = ['Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship'];
const PRIVACY_OPTS   = ['Public', 'Connections Only', 'Private'];

const SKILL_LEVEL_COLORS = {
  Beginner: '#94A3B8', Intermediate: '#3B82F6', Advanced: '#10B981', Expert: '#F59E0B',
};
const SKILL_LEVEL_PCT = { Beginner: 25, Intermediate: 50, Advanced: 75, Expert: 100 };

// ─── Helpers ─────────────────────────────────────────────────────────────────
const toStr     = v => (v === null || v === undefined ? '' : String(v));
const clampPct  = n => Math.max(0, Math.min(100, Math.round(Number.isFinite(n) ? n : 0)));
const makeId    = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;
const scoreColor = s => s >= 80 ? '#10b981' : s >= 60 ? '#3b82f6' : s >= 40 ? '#f59e0b' : '#ef4444';

// FIX: original code only showed http URIs — this allows local file://, content://, data: too
const isValidImageUri = uri => {
  if (!uri || typeof uri !== 'string') return false;
  return uri.startsWith('http') || uri.startsWith('https') ||
         uri.startsWith('file://') || uri.startsWith('content://') ||
         uri.startsWith('data:image') || uri.length > 200;
};

// ─── Defaults ────────────────────────────────────────────────────────────────
const DEFAULT_PROFILE = {
  personal: {
    profileImage: null, profileImageB64: null,
    fullName: '', email: '', phone: '', location: '',
    jobTitle: '', company: '', about: '', website: '',
    linkedin: '', github: '', twitter: '', nationality: '',
    availability: 'Available', noticePeriod: '1 month',
  },
  experience: [], education: [], skills: [], certifications: [],
  projects: [], languages: [], awards: [], volunteer: [],
  preferences: { jobType: [], salaryRange: '', workLocation: '', industries: [] },
  privacy: {
    profileVisibility: 'Public', showEmail: true, showPhone: false,
    showSalary: false, showLocation: true, allowRecruiterContact: true,
  },
};

const DEFAULT_STATS = {
  completionPercentage: 0, profileStrength: 0, profileViews: 0,
  searchAppearances: 0, connections: 0, applications: 0, interviews: 0,
  lastUpdated: new Date().toISOString(),
};


// ─── Main Component ───────────────────────────────────────────────────────────
// Module-scope field component so its identity is STABLE across renders.
// Previously LF was defined inside renderListModal, so every keystroke re-ran
// renderListModal, gave LF a new identity, and React remounted the TextInput —
// dropping focus after one character ("letter by letter"). listForm/setListForm
// arrive via props; toStr and the `st` stylesheet are already module-scoped.
const LF = ({ label, fkey, placeholder, multiline, required, listForm, setListForm }) => (
  <View style={{ marginBottom: 12 }}>
    <Text style={st.mLabel}>{label}{required && <Text style={{ color: '#ef4444' }}> *</Text>}</Text>
    <TextInput
      value={toStr(listForm[fkey])}
      onChangeText={v => setListForm(p => ({ ...p, [fkey]: v }))}
      placeholder={placeholder}
      placeholderTextColor="#94a3b8"
      style={[st.mInput, multiline && st.mInputM]}
      multiline={!!multiline}
    />
  </View>
);

export default function ProfileScreen({ navigation, route }) {

  const tabs = useMemo(() => [
    { key: 'personal',      title: 'Personal',   icon: 'person'      },
    { key: 'experience',    title: 'Experience', icon: 'briefcase'   },
    { key: 'education',     title: 'Education',  icon: 'school'      },
    { key: 'skills',        title: 'Skills',     icon: 'star'        },
    { key: 'certifications',title: 'Certs',      icon: 'ribbon'      },
    { key: 'projects',      title: 'Projects',   icon: 'code-slash'  },
    { key: 'languages',     title: 'More',       icon: 'language'    },
    { key: 'preferences',   title: 'Prefs',      icon: 'options'     },
    { key: 'ats',           title: 'ATS',        icon: 'analytics'   },
  ], []);

  const [activeTab,       setActiveTab]       = useState('personal');
  const [profileData,     setProfileData]     = useState(DEFAULT_PROFILE);
  const [profileStats,    setProfileStats]    = useState(DEFAULT_STATS);
  const [refreshing,      setRefreshing]      = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [hasUnsaved,      setHasUnsaved]      = useState(false);
  const [photoUploading,  setPhotoUploading]  = useState(false);

  // modals
  const [editModal,       setEditModal]       = useState(false);
  const [editField,       setEditField]       = useState(null);
  const [editValue,       setEditValue]       = useState('');
  const [listModal,       setListModal]       = useState(false);
  const [listType,        setListType]        = useState(null);
  const [listEditId,      setListEditId]      = useState(null);
  const [listForm,        setListForm]        = useState({});
  const [photoModal,      setPhotoModal]      = useState(false);
  const [previewModal,    setPreviewModal]    = useState(false);
  const [aiModal,         setAiModal]         = useState(false);
  const [aiLoading,       setAiLoading]       = useState(false);
  const [aiSuggestions,   setAiSuggestions]   = useState([]);

  // ATS
  const [atsResult,  setAtsResult]  = useState(null);
  const [aiRecs,     setAiRecs]     = useState([]);
  const [atsLoading, setAtsLoading] = useState(false);

  const fadeAnim  = useRef(new Animated.Value(0)).current;
  const autoTimer = useRef(null);
  const prevData  = useRef(null);

  // ── Init ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadAll();
    Animated.timing(fadeAnim, { toValue: 1, duration: 400, useNativeDriver: true }).start();
  }, []);

  // Reload every time the screen comes into focus
  // This ensures data synced from BuildResume, Dashboard, or Login is picked up immediately
  useFocusEffect(
    useCallback(() => {
      // Only reload if we already have data (avoids double-load on first mount)
      if (prevData.current) loadAll();
    }, [loadAll])
  );

  // Track changes
  useEffect(() => {
    if (prevData.current && JSON.stringify(prevData.current) !== JSON.stringify(profileData)) {
      setHasUnsaved(true);
    }
  }, [profileData]);

  // Auto-save 2 seconds after last change (no toast)
  useEffect(() => {
    if (!hasUnsaved) return;
    if (autoTimer.current) clearTimeout(autoTimer.current);
    autoTimer.current = setTimeout(() => saveAll(profileData, false), 2000);
    return () => clearTimeout(autoTimer.current);
  }, [profileData, hasUnsaved]);

  // ── Load ────────────────────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    try {
      // Pre-populate from route params (Dashboard navigation)
      const routeUser = route?.params?.userData;
      if (routeUser) {
        const fullName = [routeUser.first_name || '', routeUser.last_name || '']
          .filter(Boolean).join(' ') || routeUser.full_name || routeUser.name || '';
        setProfileData(prev => ({
          ...prev,
          personal: {
            ...prev.personal,
            fullName:     fullName            || prev.personal.fullName,
            email:        routeUser.email     || prev.personal.email,
            phone:        routeUser.phone     || prev.personal.phone,
            jobTitle:     routeUser.job_title || routeUser.position || prev.personal.jobTitle,
            company:      routeUser.company   || prev.personal.company,
            location:     routeUser.location  || prev.personal.location,
            profileImage: routeUser.profile_image || routeUser.avatar || prev.personal.profileImage,
          },
        }));
      }

      // Try the unified service first (backend → cache fallback inside the service)
      try {
        await ProfileManagementService.bootstrap();
        const svcResult = await ProfileManagementService.getProfile();
        if (svcResult.success && svcResult.data) {
          const sd = svcResult.data;
          // The service may return either the legacy DB shape (full_name, job_title)
          // or the unified shape (personalInfo.fullName, profession). Handle both.
          const pi = sd.personalInfo || sd;
          setProfileData(prev => ({
            ...prev,
            personal: {
              ...prev.personal,
              fullName:    pi.fullName || pi.name || pi.full_name || prev.personal.fullName,
              email:       pi.email    || prev.personal.email,
              phone:       pi.phone    || prev.personal.phone,
              location:    pi.location || prev.personal.location,
              jobTitle:    sd.profession || pi.jobTitle || pi.job_title || pi.position || prev.personal.jobTitle,
              company:     pi.company  || prev.personal.company,
              about:       sd.summary  || sd.professionalSummary || pi.about || pi.bio || prev.personal.about,
              linkedin:    pi.linkedin || prev.personal.linkedin,
              github:      pi.github   || prev.personal.github,
              website:     pi.website  || pi.portfolio || prev.personal.website,
              nationality: pi.nationality || prev.personal.nationality,
              profileImage: pi.profileImage || pi.profile_image || prev.personal.profileImage,
            },
            experience:     Array.isArray(sd.experience)     ? sd.experience     : (prev.experience     || []),
            education:      Array.isArray(sd.education)      ? sd.education      : (prev.education      || []),
            skills:         Array.isArray(sd.skills)         ? sd.skills         : (prev.skills         || []),
            certifications: Array.isArray(sd.certifications) ? sd.certifications : (prev.certifications || []),
            projects:       Array.isArray(sd.projects)       ? sd.projects       : (prev.projects       || []),
            languages:      Array.isArray(sd.languages)      ? sd.languages      : (prev.languages      || []),
            awards:         Array.isArray(sd.awards)         ? sd.awards         : (prev.awards         || []),
            volunteer:      Array.isArray(sd.volunteer)      ? sd.volunteer      : (prev.volunteer      || []),
          }));
        }
      } catch (_) { /* service may be unavailable; continue with cache below */ }

      // Read from all storage keys (written by Login/Signup/Dashboard)
      const candidates = await Promise.all([
        AsyncStorage.getItem(PROF_KEYS.PROFILE),
        AsyncStorage.getItem('userProfile'),
        AsyncStorage.getItem('professional_default_userProfile'),
      ]);
      const raw = candidates.find(Boolean);
      if (raw) {
        const p = JSON.parse(raw);
        setProfileData(prev => ({
          ...DEFAULT_PROFILE, ...p,
          personal:     { ...DEFAULT_PROFILE.personal,     ...(p.personal     || {}) },
          preferences:  { ...DEFAULT_PROFILE.preferences,  ...(p.preferences  || {}) },
          privacy:      { ...DEFAULT_PROFILE.privacy,       ...(p.privacy      || {}) },
          experience:     p.experience     || [],
          education:      p.education      || [],
          skills:         p.skills         || [],
          certifications: p.certifications || [],
          projects:       p.projects       || [],
          languages:      p.languages      || [],
          awards:         p.awards         || [],
          volunteer:      p.volunteer      || [],
        }));
      }

      // Restore persisted base64 photo (survives app restarts even with local URIs)
      // ── Load user identity from Login / Signup keys ────────────────────────
      // Login writes: professional_default_user_data, auth_token
      // Signup writes: professional_default_user_data, professional_default_user
      // We read all possible keys and merge — profile keys take priority
      const userDataCandidates = await Promise.all([
        AsyncStorage.getItem('prof_user_data'),
        AsyncStorage.getItem('user_data'),
        AsyncStorage.getItem('professional_default_user_data'),
        AsyncStorage.getItem('professional_default_user'),
      ]);
      const userDataRaw = userDataCandidates.find(Boolean);
      if (userDataRaw) {
        try {
          const u = JSON.parse(userDataRaw);
          const fullName = [u.first_name || '', u.last_name || ''].filter(Boolean).join(' ')
            || u.full_name || u.name || '';
          setProfileData(prev => {
            // Only fill fields that are currently empty — don't overwrite profile edits
            const p = prev.personal || {};
            return {
              ...prev,
              personal: {
                ...p,
                fullName:     p.fullName     || fullName             || '',
                email:        p.email        || u.email              || '',
                phone:        p.phone        || u.phone              || '',
                jobTitle:     p.jobTitle     || u.job_title || u.position || u.current_position || '',
                company:      p.company      || u.company            || '',
                location:     p.location     || u.location           || '',
                profileImage: p.profileImage || u.profile_image || u.avatar || null,
                nationality:  p.nationality  || u.nationality        || '',
              },
            };
          });
        } catch {}
      }

      // ── Restore persisted photo (profile_photo > resume_photo) ─────────────
      const [profilePhotoUri, profilePhotoB64, resumePhotoUri, resumePhotoB64] = await Promise.all([
        AsyncStorage.getItem('profile_photo_uri'),
        AsyncStorage.getItem('profile_photo_b64'),
        AsyncStorage.getItem('resume_photo_uri'),   // written by BuildResumeScreen
        AsyncStorage.getItem('resume_photo_b64'),   // written by BuildResumeScreen
      ]);
      // Profile photo takes priority; resume photo is the fallback
      const bestUri = profilePhotoUri || resumePhotoUri || null;
      const bestB64 = profilePhotoB64 || resumePhotoB64 || null;
      if (bestUri || bestB64) {
        setProfileData(prev => ({
          ...prev,
          personal: {
            ...prev.personal,
            profileImage:    bestUri || prev.personal.profileImage,
            profileImageB64: bestB64 || prev.personal.profileImageB64,
          },
        }));
      }

      const statsRaw = await AsyncStorage.getItem('profileStats');
      if (statsRaw) setProfileStats(prev => ({ ...prev, ...JSON.parse(statsRaw) }));

      const atsRaw  = await AsyncStorage.getItem('atsResult');
      const recsRaw = await AsyncStorage.getItem('aiRecommendations');
      if (atsRaw)  setAtsResult(JSON.parse(atsRaw));
      if (recsRaw) setAiRecs(JSON.parse(recsRaw));

      // Pull live API stats
      // Load from real DB (enriches AsyncStorage data with live DB data)
      loadProfileFromDB().catch(() => {});
    } catch (e) { console.warn('loadAll:', e && e.message); }
  }, []);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadAll();
    setRefreshing(false);
  }, [loadAll]);

  // ── Score calculations ───────────────────────────────────────────────────────
  const calcCompletion = useCallback(data => {
    const p = data?.personal || {};
    const fields = ['fullName', 'email', 'phone', 'location', 'jobTitle', 'company', 'about'];
    let done = fields.filter(k => toStr(p[k]).trim()).length;
    const total = fields.length + 1; // +1 for photo
    if (p.profileImage || p.profileImageB64) done++;
    let bonus = 0;
    if ((data?.experience     || []).length > 0) bonus += 8;
    if ((data?.education      || []).length > 0) bonus += 5;
    const sk = (data?.skills  || []).length;
    if (sk >= 5) bonus += 10; else if (sk > 0) bonus += 4;
    if ((data?.certifications || []).length > 0) bonus += 4;
    if ((data?.projects       || []).length > 0) bonus += 3;
    if ((data?.languages      || []).length > 0) bonus += 3;
    if (toStr(p.linkedin).trim())               bonus += 5;
    return clampPct(Math.round((done / total) * 100) + bonus);
  }, []);

  const calcStrength = useCallback(data => {
    const p = data?.personal || {};
    let s = 0;
    if (toStr(p.fullName).trim())                 s += 5;
    if (toStr(p.email).trim())                    s += 5;
    if (toStr(p.phone).trim())                    s += 5;
    if (toStr(p.location).trim())                 s += 5;
    if (toStr(p.jobTitle).trim())                 s += 10;
    if (toStr(p.company).trim())                  s += 5;
    if (toStr(p.about).trim().length > 50)        s += 8;
    if (p.profileImage || p.profileImageB64)      s += 10;
    if (toStr(p.linkedin).trim())                 s += 8;
    if (toStr(p.github).trim())                   s += 4;
    if (toStr(p.website).trim())                  s += 3;
    s += Math.min((data?.experience     || []).length * 5, 15);
    s += Math.min((data?.skills         || []).length * 2, 10);
    if ((data?.education      || []).length > 0)  s += 5;
    if ((data?.certifications || []).length > 0)  s += 5;
    if ((data?.projects       || []).length > 0)  s += 3;
    return clampPct(s);
  }, []);


  // ── Resolve user ID from storage ─────────────────────────────────────────
  const resolveUserId = async () => {
    // 1) Flat numeric keys (fastest path)
    for (const k of ['userId', 'user_id', 'professional_user_id']) {
      const v = await AsyncStorage.getItem(k);
      const n = parseInt(v, 10);
      if (v && !isNaN(n) && n > 0) return n;
    }
    // 2) Fall back to the numeric id inside the user object written by
    //    Login/Signup/Dashboard. Without this, syncItemToDB + loadProfileFromDB
    //    silently bail and profile data never reaches the database.
    for (const k of ['prof_user_data', 'user_data', 'professional_default_user_data',
                     'professional_default_user', 'user', 'userData']) {
      try {
        const raw = await AsyncStorage.getItem(k);
        if (!raw) continue;
        const u = JSON.parse(raw);
        const cand = u?.id ?? u?.user_id ?? u?.userId ?? u?.user?.id ?? u?.data?.user?.id;
        const n = parseInt(cand, 10);
        if (!isNaN(n) && n > 0) {
          // Cache so the flat-key path succeeds next time.
          AsyncStorage.setItem('user_id', String(n)).catch(() => {});
          return n;
        }
      } catch (_) { /* try next key */ }
    }
    // 3) Last resort: resolve from the session token via the server.
    //    Works on every login path even when nothing usable is stored locally.
    try {
      const token = await AsyncStorage.getItem('auth_token')
        || await AsyncStorage.getItem('userToken')
        || await AsyncStorage.getItem('token');
      if (token) {
        const res = await fetch(`${API_BASE}/api/me`, {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': API_KEY,
            Authorization: `Bearer ${token}`,
          },
        });
        if (res.ok) {
          const j = await res.json();
          const n = parseInt(j?.id ?? j?.user_id, 10);
          if (!isNaN(n) && n > 0) {
            AsyncStorage.setItem('user_id', String(n)).catch(() => {});
            return n;
          }
        }
      }
    } catch (_) { /* offline / endpoint unavailable */ }
    return null;
  };

  // ── Skill level string to DB integer ─────────────────────────────────────
  const SKILL_LEVEL_NUM = {Beginner:1,Intermediate:2,Advanced:3,Expert:4};

  // ── Sync one list item to its DB sub-table ────────────────────────────────
  const syncItemToDB = async (section, item, action) => {
    try {
      const uid = await resolveUserId();
      if (!uid) return;
      const dbId = item && item._dbId ? item._dbId : null;
      const del  = action === 'delete';

      if (section === 'experience') {
        const base = '/api/users/' + uid + '/experience';
        if (del && dbId) { await apiFetch(base + '/' + dbId, {method:'DELETE'}); return; }
        const desc = (item.description||'') + (item.achievements ? ' ' + item.achievements : '');
        const payload = { company_name:item.company||'', job_title:item.title||'',
          start_date:item.startDate||null,
          end_date:item.current ? null : (item.endDate||null),
          is_current:!!item.current, description:desc, location:item.location||'' };
        const r = dbId
          ? await apiFetch(base+'/'+dbId,{method:'PUT',body:JSON.stringify(payload)})
          : await apiFetch(base,{method:'POST',body:JSON.stringify(payload)});
        if (!r.error && r.data && r.data.id) {
          setProfileData(prev => ({...prev, experience:prev.experience.map(e => e.id===item.id ? {...e,_dbId:r.data.id} : e)}));
        }
      } else if (section === 'skills') {
        const base = '/api/users/' + uid + '/skills';
        if (del && dbId) { await apiFetch(base+'/'+dbId,{method:'DELETE'}); return; }
        const payload = { skill_name:item.name||'', skill_level:SKILL_LEVEL_NUM[item.level]||2 };
        const r = dbId
          ? await apiFetch(base+'/'+dbId,{method:'PUT',body:JSON.stringify(payload)})
          : await apiFetch(base,{method:'POST',body:JSON.stringify(payload)});
        if (!r.error && r.data && r.data.id) {
          setProfileData(prev => ({...prev, skills:prev.skills.map(s => s.id===item.id ? {...s,_dbId:r.data.id} : s)}));
        }
      } else if (section === 'certifications') {
        const base = '/api/users/' + uid + '/certifications';
        if (del && dbId) { await apiFetch(base+'/'+dbId,{method:'DELETE'}); return; }
        const payload = { cert_name:item.name||'', issuing_organization:item.issuer||'',
          issue_date:item.date||null, expiry_date:item.expiry||null,
          credential_id:item.credentialId||'', credential_url:item.url||'' };
        const r = dbId
          ? await apiFetch(base+'/'+dbId,{method:'PUT',body:JSON.stringify(payload)})
          : await apiFetch(base,{method:'POST',body:JSON.stringify(payload)});
        if (!r.error && r.data && r.data.id) {
          setProfileData(prev => ({...prev, certifications:prev.certifications.map(c => c.id===item.id ? {...c,_dbId:r.data.id} : c)}));
        }
      } else if (section === 'education') {
        const base = '/api/users/' + uid + '/education';
        if (del && dbId) { await apiFetch(base+'/'+dbId,{method:'DELETE'}); return; }
        const payload = { institution:item.institution||'', degree:item.degree||'',
          field_of_study:'', start_date:item.startDate||null, end_date:item.endDate||null,
          grade:item.gpa||'', description:item.description||'' };
        const r = dbId
          ? await apiFetch(base+'/'+dbId,{method:'PUT',body:JSON.stringify(payload)})
          : await apiFetch(base,{method:'POST',body:JSON.stringify(payload)});
        if (!r.error && r.data && r.data.id) {
          setProfileData(prev => ({...prev, education:prev.education.map(e => e.id===item.id ? {...e,_dbId:r.data.id} : e)}));
        }
      } else if (section === 'languages') {
        const base = '/api/users/' + uid + '/languages';
        if (del && dbId) { await apiFetch(base+'/'+dbId,{method:'DELETE'}); return; }
        const payload = { language_name:item.name||'', proficiency_level:(item.level||'conversational').toLowerCase() };
        const r = await apiFetch(base,{method:'POST',body:JSON.stringify(payload)});
        if (!r.error && r.data && r.data.id) {
          setProfileData(prev => ({...prev, languages:prev.languages.map(l => l.id===item.id ? {...l,_dbId:r.data.id} : l)}));
        }
      }
    } catch(e) { console.warn('syncItemToDB:', section, e && e.message); }
  };

  // ── Load full profile from DB (unified endpoint returns all sub-tables) ───
  const loadProfileFromDB = async () => {
    try {
      const uid = await resolveUserId();
      console.log('[ProfileLoad] resolveUserId =>', uid);
      if (!uid) return;
      const r = await apiFetch('/api/users/' + uid + '/profile');
      console.log('[ProfileLoad] fetch error?', r && r.error,
        '| exp:', r && r.data && (r.data.work_experience || []).length,
        '| edu:', r && r.data && (r.data.education || []).length,
        '| skills:', r && r.data && (r.data.skills || []).length);
      if (!r.error && r.data) {
        const d = r.data;
        const mapSkillLevel = n => ['Beginner','Intermediate','Advanced','Expert'][Math.min(Math.max((n||2)-1,0),3)] || 'Intermediate';
        setProfileData(prev => ({
          ...prev,
          personal: {
            ...prev.personal,
            fullName:    d.full_name||[d.first_name||'',d.last_name||''].filter(Boolean).join(' ')||prev.personal.fullName,
            email:       d.email       || prev.personal.email,
            phone:       d.phone       || prev.personal.phone,
            location:    d.location    || prev.personal.location,
            jobTitle:    d.job_title||d.position || prev.personal.jobTitle,
            company:     d.company     || prev.personal.company,
            about:       d.bio||d.about|| prev.personal.about,
            linkedin:    d.linkedin    || prev.personal.linkedin,
            github:      d.github      || prev.personal.github,
            website:     d.personal_website||d.portfolio_url || prev.personal.website,
            nationality: d.nationality || prev.personal.nationality,
            profileImage:d.profile_image||d.avatar || prev.personal.profileImage,
          },
          skills: d.skills && d.skills.length > 0 ? d.skills.map(s => ({
            id:String(s.id), _dbId:s.id, name:s.skill_name||'', level:mapSkillLevel(s.skill_level),
          })) : prev.skills,
          certifications: d.certifications && d.certifications.length > 0 ? d.certifications.map(c => ({
            id:String(c.id), _dbId:c.id, name:c.cert_name||'', issuer:c.issuing_organization||'',
            date:c.issue_date ? String(c.issue_date).split('T')[0] : '',
            expiry:c.expiry_date ? String(c.expiry_date).split('T')[0] : '',
            credentialId:c.credential_id||'', url:c.credential_url||'',
          })) : prev.certifications,
          education: d.education && d.education.length > 0 ? d.education.map(e => ({
            id:String(e.id), _dbId:e.id, degree:e.degree||'', institution:e.institution||'',
            location:'', startDate:e.start_date ? String(e.start_date) : '',
            endDate:e.end_date ? String(e.end_date) : '', gpa:e.grade||'', honors:'', description:e.description||'',
          })) : prev.education,
          experience: d.work_experience && d.work_experience.length > 0 ? d.work_experience.map(e => ({
            id:String(e.id), _dbId:e.id, title:e.job_title||'', company:e.company_name||'',
            location:e.location||'', startDate:e.start_date ? String(e.start_date).split('T')[0] : '',
            endDate:e.end_date ? String(e.end_date).split('T')[0] : '',
            current:!!e.is_current, description:e.description||'', achievements:'',
          })) : prev.experience,
          languages: d.languages && d.languages.length > 0 ? d.languages.map(l => ({
            id:String(l.id), _dbId:l.id, name:l.language_name||'',
            level:(l.proficiency_level||'conversational').charAt(0).toUpperCase()+(l.proficiency_level||'conversational').slice(1),
          })) : prev.languages,
        }));
        if (d.id) {
          await AsyncStorage.setItem('userId', String(d.id));
          await AsyncStorage.setItem('user_id', String(d.id));
        }
        if (d.email) await AsyncStorage.setItem('user_email', d.email);
      }
    } catch(e) { console.warn('loadProfileFromDB:', e && e.message); }
  };

  // ── Save to all keys + API ───────────────────────────────────────────────────
  const saveAll = useCallback(async (data = profileData, showAlert = true) => {
    setSaving(true);
    try {
      const completion = calcCompletion(data);
      const strength   = calcStrength(data);
      const nextStats  = {
        ...profileStats,
        completionPercentage: completion,
        profileStrength:      strength,
        lastUpdated:          new Date().toISOString(),
      };
      setProfileStats(nextStats);

      const profileJson = JSON.stringify(data);
      const statsJson   = JSON.stringify(nextStats);

      // Write to ALL keys so Home/Dashboard/Login always read fresh data
      await Promise.all([
        AsyncStorage.setItem(PROF_KEYS.PROFILE,                     profileJson),
        AsyncStorage.setItem('userProfile',                          profileJson),
        AsyncStorage.setItem('professional_default_userProfile',     profileJson),
        AsyncStorage.setItem('profileStats',                         statsJson),
      ]);

      // Unified service sync — feeds the same data into UploadResumeScreen,
      // BuildResumeScreen, ResumePreviewScreen and any other consumer of
      // ProfileManagementService.getProfile().
      try {
        await ProfileManagementService.saveProfile({
          personalInfo: data.personal || {},
          profession:     data.personal?.jobTitle || '',
          summary:        data.personal?.about    || '',
          experience:     data.experience     || [],
          education:      data.education      || [],
          skills:         data.skills         || [],
          certifications: data.certifications || [],
          projects:       data.projects       || [],
          languages:      data.languages      || [],
          awards:         data.awards         || [],
          volunteer:      data.volunteer      || [],
        });
      } catch (_) { /* service is best-effort; local writes already happened */ }

      // Dashboard sync payload (useFocusEffect in Home + Dashboard reads this)
      const dash = {
        user: {
          fullName:     data.personal?.fullName     || '',
          email:        data.personal?.email        || '',
          jobTitle:     data.personal?.jobTitle     || '',
          company:      data.personal?.company      || '',
          location:     data.personal?.location     || '',
          profileImage: data.personal?.profileImageB64 || data.personal?.profileImage || null,
          availability: data.personal?.availability || 'Available',
        },
        stats: {
          ...nextStats,
          completion_percentage: completion,
          profile_strength:      strength,
        },
        skillsCount:         (data.skills        || []).length,
        experienceCount:     (data.experience    || []).length,
        educationCount:      (data.education     || []).length,
        certificationsCount: (data.certifications|| []).length,
        lastUpdated:         new Date().toISOString(),
      };
      const dashJson = JSON.stringify(dash);
      await AsyncStorage.setItem(PROF_KEYS.DASH,   dashJson);
      await AsyncStorage.setItem('dashboardData',  dashJson);

      // Push to real DB tables (fire-and-forget)
      resolveUserId().then(uid => {
        if (!uid) return;
        const p = data.personal || {};
        const usersPayload = {
          full_name:p.fullName||'', email:p.email||'', phone:p.phone||'',
          bio:p.about||'', about:p.about||'', location:p.location||'',
          job_title:p.jobTitle||'', position:p.jobTitle||'',
          company:p.company||'',
          linkedin:p.linkedin||'', github:p.github||'',
          personal_website:p.website||'', nationality:p.nationality||'',
          profile_image:p.profileImageB64||p.profileImage||'',
        };
        apiFetch('/api/users/'+uid, {method:'PUT',body:JSON.stringify(usersPayload)}).catch(()=>{});
        apiFetch('/api/business-users/'+uid, {method:'PUT',body:JSON.stringify({...usersPayload,linkedin_url:p.linkedin||''})}).catch(()=>{});
      }).catch(()=>{});

      setHasUnsaved(false);
      prevData.current = JSON.parse(profileJson);
      if (showAlert) Alert.alert('✅ Saved', 'Your profile has been saved successfully.');
    } catch (e) {
      Alert.alert('Save Error', e?.message || 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  }, [profileData, profileStats, calcCompletion, calcStrength]);


  // ── Photo: pick + base64 + API upload ────────────────────────────────────────
  const pickPhoto = async (source) => {
    // Step 1 — close modal FIRST (must happen before any async work on Android)
    setPhotoModal(false);

    if (!ImagePicker) {
      Alert.alert('Install required', 'Run: npx expo install expo-image-picker');
      return;
    }

    // Step 2 — wait for modal animation to fully finish before launching picker
    // Without this delay the Android picker fails silently
    await new Promise(resolve => setTimeout(resolve, Platform.OS === 'android' ? 500 : 200));

    try {
      // Step 3 — request permission
      let perm;
      try {
        perm = source === 'camera'
          ? await ImagePicker.requestCameraPermissionsAsync()
          : await ImagePicker.requestMediaLibraryPermissionsAsync();
      } catch (permErr) {
        console.warn('Permission request error:', permErr?.message);
        Alert.alert('Permission Error', 'Could not request permission. Try again.');
        return;
      }

      // On Android some SDK versions return 'undetermined' on first call —
      // treat it as needing to ask (the dialog will have shown already)
      if (perm?.status === 'denied' && !perm?.canAskAgain) {
        Alert.alert(
          'Permission Blocked',
          source === 'camera'
            ? 'Camera access is blocked. Please enable it in your device Settings → App → Permissions.'
            : 'Photo library access is blocked. Please enable it in your device Settings → App → Permissions.',
          [{ text: 'OK' }]
        );
        return;
      }

      if (perm?.status !== 'granted') {
        // Permission was not granted — show informative message but don't block
        // (the system dialog already ran and the user may have just dismissed it)
        console.warn('Permission status:', perm?.status);
        if (perm?.status === 'denied') {
          Alert.alert('Permission Required',
            'Please allow access when prompted, or enable it in Settings.');
          return;
        }
      }

      // Step 4 — launch picker WITHOUT base64 (b64 in picker causes crashes on
      // many Android devices due to memory pressure; we encode separately after)
      const pickerOptions = {
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.85,
        // base64: false intentionally — we read & encode the file after picking
      };

      let result;
      if (source === 'camera') {
        result = await ImagePicker.launchCameraAsync(pickerOptions);
      } else {
        // Handle SDK version differences for mediaTypes
        const opts = { ...pickerOptions };
        if (ImagePicker.MediaTypeOptions?.Images !== undefined) {
          opts.mediaTypes = ImagePicker.MediaTypeOptions.Images; // SDK ≤ 51
        } else if (ImagePicker.MediaType?.IMAGE !== undefined) {
          opts.mediaTypes = [ImagePicker.MediaType.IMAGE];       // SDK 52+
        }
        result = await ImagePicker.launchImageLibraryAsync(opts);
      }

      // Step 5 — handle result
      if (result?.canceled === true) return;          // user cancelled — silent
      if (!result?.assets?.[0]?.uri) return;         // no asset — silent

      const uri = result.assets[0].uri;

      // Update display immediately with the local URI
      setProfileData(prev => ({
        ...prev,
        personal: {
          ...prev.personal,
          profileImage: uri,
        },
      }));

      // Persist URI so it survives navigation
      await AsyncStorage.setItem('profile_photo_uri', uri);

      // Step 6 — encode to base64 AFTER picking (background, non-blocking)
      // This keeps the picker fast and avoids memory crashes
      setPhotoUploading(true);
      try {
        // Use expo-file-system if available for reliable base64 encoding
        let b64 = null;
        try {
          const FileSystem = require('expo-file-system/legacy');
          const fileInfo = await FileSystem.readAsStringAsync(uri, {
            encoding: FileSystem.EncodingType.Base64,
          });
          const mimeType = uri.endsWith('.png') ? 'image/png' : 'image/jpeg';
          b64 = `data:${mimeType};base64,${fileInfo}`;
        } catch {
          // expo-file-system not available — skip base64
          b64 = null;
        }

        if (b64) {
          // Update state with b64 for PDF export
          setProfileData(prev => ({
            ...prev,
            personal: { ...prev.personal, profileImageB64: b64 },
          }));
          await AsyncStorage.setItem('profile_photo_b64', b64);

          // Sync photo to DB
          resolveUserId().then(uid => {
            if (!uid) return;
            apiFetch('/api/users/'+uid, {method:'PUT',body:JSON.stringify({profile_image:b64})}).catch(()=>{});
            apiFetch('/api/business-users/'+uid, {method:'PUT',body:JSON.stringify({profile_image:b64})}).catch(()=>{});
          }).catch(()=>{});
        }
      } catch (encErr) {
        console.warn('Base64 encoding error:', encErr?.message);
        // Non-fatal — image still displays via local URI
      } finally {
        setPhotoUploading(false);
      }

    } catch (e) {
      setPhotoUploading(false);
      console.warn('pickPhoto error:', e?.message);
      Alert.alert('Error', 'Could not open photo picker. Please try again.');
    }
  };

  // Pull photo from BuildResumeScreen (fallback when picker isn't working)
  const useResumePhoto = async () => {
    setPhotoModal(false);
    try {
      const [resumeUri, resumeB64] = await Promise.all([
        AsyncStorage.getItem('resume_photo_uri'),
        AsyncStorage.getItem('resume_photo_b64'),
      ]);
      if (!resumeUri && !resumeB64) {
        Alert.alert('No resume photo', 'Add a photo on the Build Resume screen first.');
        return;
      }
      setProfileData(prev => ({
        ...prev,
        personal: {
          ...prev.personal,
          profileImage: resumeUri || resumeB64,
          profileImageB64: resumeB64 || prev.personal.profileImageB64,
        },
      }));
      if (resumeUri) await AsyncStorage.setItem('profile_photo_uri', resumeUri);
      if (resumeB64) await AsyncStorage.setItem('profile_photo_b64', resumeB64);
    } catch (e) {
      console.warn('useResumePhoto:', e?.message);
      Alert.alert('Error', 'Could not load resume photo.');
    }
  };

  const removePhoto = () => {
    Alert.alert('Remove Photo', 'Remove your profile photo?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Remove', style: 'destructive', onPress: () => {
        setProfileData(prev => ({
          ...prev,
          personal: { ...prev.personal, profileImage: null, profileImageB64: null },
        }));
        AsyncStorage.removeItem('profile_photo_b64').catch(() => {});
        setPhotoModal(false);
      }},
    ]);
  };

  // ── Field edit modal ─────────────────────────────────────────────────────────
  const openEdit = (fieldPath, currentValue) => {
    setEditField(fieldPath);
    setEditValue(toStr(currentValue));
    setEditModal(true);
  };

  const applyEdit = () => {
    if (!editField) return;
    const [group, key] = editField.split('.');
    setProfileData(prev => {
      const next = { ...prev };
      if (group === 'personal') {
        next.personal = { ...next.personal, [key]: editValue };
      } else if (group === 'preferences') {
        if (key === 'jobType' || key === 'industries') {
          next.preferences = {
            ...next.preferences,
            [key]: editValue.split(',').map(x => x.trim()).filter(Boolean),
          };
        } else {
          next.preferences = { ...next.preferences, [key]: editValue };
        }
      }
      return next;
    });
    setEditModal(false);
    setEditField(null);
    setEditValue('');
  };

  // ── List CRUD ────────────────────────────────────────────────────────────────
  const openListModal = (type, item = null) => {
    setListType(type);
    setListEditId(item?.id || null);
    const defaults = {
      experience:    { title:'', company:'', location:'', startDate:'', endDate:'', current:false, description:'', achievements:'' },
      education:     { degree:'', institution:'', location:'', startDate:'', endDate:'', gpa:'', honors:'', description:'' },
      certification: { name:'', issuer:'', date:'', expiry:'', credentialId:'', url:'' },
      skill:         { name:'', level:'Intermediate' },
      project:       { name:'', description:'', url:'', tech:'', startDate:'', endDate:'' },
      language:      { name:'', level:'Conversational' },
      award:         { title:'', issuer:'', date:'', description:'' },
      volunteer:     { role:'', org:'', startDate:'', endDate:'', description:'' },
    };
    const form = { ...(defaults[type] || {}) };
    if (item) Object.keys(form).forEach(k => { if (item[k] !== undefined) form[k] = item[k]; });
    setListForm(form);
    setListModal(true);
  };

  const closeListModal = () => {
    setListModal(false); setListType(null); setListEditId(null); setListForm({});
  };

  const upsertListItem = () => {
    const type = listType;
    if (!type) return;
    const id = listEditId || makeId();

    const validators = {
      experience:    () => listForm.title && listForm.company,
      education:     () => listForm.degree && listForm.institution,
      certification: () => listForm.name && listForm.issuer,
      skill:         () => !!listForm.name,
      project:       () => !!listForm.name,
      language:      () => !!listForm.name,
      award:         () => !!listForm.title,
      volunteer:     () => listForm.role && listForm.org,
    };
    if (!validators[type]?.()) {
      Alert.alert('Missing fields', 'Please fill in the required fields.'); return;
    }

    const keyMap = {
      experience: 'experience', education: 'education', certification: 'certifications',
      skill: 'skills', project: 'projects', language: 'languages',
      award: 'awards', volunteer: 'volunteer',
    };
    const listKey = keyMap[type];

    setProfileData(prev => {
      let list = [...(prev[listKey] || [])];
      // Normalise legacy string skills
      if (type === 'skill') {
        list = list.map(s => typeof s === 'string' ? { id: makeId(), name: s, level: 'Intermediate' } : s);
      }
      const idx = list.findIndex(x => (x.id || x) === id);
      const item = { id, ...listForm };
      if (idx >= 0) list[idx] = item; else list.unshift(item);
      // Preserve _dbId from existing item
      const existing = list.find(x => (x.id||x) === id);
      if (existing && existing._dbId) item._dbId = existing._dbId;
      return { ...prev, [listKey]: list };
    });
    // Sync to DB (fire-and-forget)
    const DB_SECTIONS = ['experience','skills','certifications','education','languages'];
    const dbSection = keyMap[type];
    if (dbSection && DB_SECTIONS.indexOf(dbSection) >= 0) {
      syncItemToDB(dbSection, { id, ...listForm }, 'upsert').catch(() => {});
    }
    closeListModal();
  };

  const deleteItem = (listKey, id) => {
    Alert.alert('Delete', 'Remove this entry?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => {
        setProfileData(prev => {
          const item = (prev[listKey] || []).find(x => (x.id || x) === id);
          const DB_SECTIONS = ['experience','skills','certifications','education','languages'];
          if (item && DB_SECTIONS.indexOf(listKey) >= 0) {
            syncItemToDB(listKey, item, 'delete').catch(() => {});
          }
          return { ...prev, [listKey]: (prev[listKey] || []).filter(x => (x.id || x) !== id) };
        });
      }},
    ]);
  };

  // ── AI About generator ───────────────────────────────────────────────────────
  const generateAbout = async () => {
    setAiLoading(true);
    setAiModal(true);
    try {
      const r = await apiFetch('/api/ai/generate-about', {
        method: 'POST',
        body: JSON.stringify({
          jobTitle:   profileData.personal.jobTitle,
          company:    profileData.personal.company,
          experience: (profileData.experience || []).slice(0, 2).map(e => `${e.title} at ${e.company}`),
          skills:     (profileData.skills     || []).slice(0, 6).map(s => s.name || s),
        }),
      });
      if (!r.error && r.suggestions) { setAiSuggestions(r.suggestions); setAiLoading(false); return; }
    } catch {}
    // Local fallback
    const role = profileData.personal.jobTitle || 'engineer';
    const sk   = (profileData.skills || []).slice(0, 3).map(s => s.name || s).join(', ') || 'engineering tools';
    const yr   = Math.min((profileData.experience || []).length * 2, 10);
    setAiSuggestions([
      `Dedicated ${role} with ${yr}+ years of experience delivering high-impact technical solutions. Proficient in ${sk}, with a proven ability to lead projects from concept to completion while collaborating across cross-functional teams.`,
      `Results-driven ${role} specialising in innovative design and operational excellence. Skilled in ${sk}, I combine deep technical knowledge with strong stakeholder communication to consistently exceed project goals.`,
      `Motivated ${role} with expertise in ${sk}. I bring a rigorous approach to problem-solving and a passion for scalable solutions that create measurable business impact.`,
    ]);
    setAiLoading(false);
  };

  // ── ATS ──────────────────────────────────────────────────────────────────────
  const runAts = useCallback(async () => {
    setAtsLoading(true);
    try {
      const skills   = (profileData.skills || []).map(s => typeof s === 'string' ? s : s?.name).filter(Boolean);
      const hasAbout = toStr(profileData.personal?.about).trim().length >= 80;
      const hasExp   = (profileData.experience    || []).length > 0;
      const hasPhoto = !!(profileData.personal?.profileImage || profileData.personal?.profileImageB64);
      const hasCerts = (profileData.certifications|| []).length > 0;

      const score = clampPct(
        30 + Math.min(skills.length * 4, 24)
           + (hasAbout ? 15 : 0) + (hasExp ? 20 : 0)
           + (hasPhoto ? 5  : 0) + (hasCerts ? 6 : 0)
      );

      const result = {
        score,
        keywords: {
          present: skills.slice(0, 12),
          missing: skills.length < 8
            ? ['Project Management', 'Documentation', 'Communication', 'Safety', 'ISO Standards']
            : [],
        },
        notes: [
          hasAbout ? '✅ About section length looks good.' : '⚠️ About section is short — aim for 80–150 words.',
          hasExp   ? '✅ Work experience present.'          : '⚠️ No experience added yet.',
          hasPhoto ? '✅ Profile photo uploaded.'           : '⚠️ Add a professional photo (+5 ATS points).',
          skills.length >= 5 ? '✅ Good skill coverage.'   : '⚠️ Add at least 5 skills.',
          hasCerts ? '✅ Certifications present.'           : '⚠️ Add relevant certifications.',
        ],
        updatedAt: new Date().toISOString(),
      };

      const recs = [];
      if (clampPct(profileStats.completionPercentage) < 80) recs.push('Complete all profile fields — recruiters skip incomplete profiles.');
      if (!hasAbout)  recs.push('Expand About to 80–150 words using role-relevant keywords.');
      if (skills.length < 5) recs.push('Add at least 5 skills (technical + soft).');
      if (!hasExp)    recs.push('Add work experience with measurable achievements (%, £, time).');
      if (!toStr(profileData.personal?.linkedin).trim()) recs.push('Add your LinkedIn URL to boost recruiter confidence.');
      if (result.keywords.missing.length) recs.push(`Consider adding: ${result.keywords.missing.slice(0, 4).join(', ')}.`);

      setAtsResult(result);
      setAiRecs(recs);
      await AsyncStorage.setItem('atsResult',         JSON.stringify(result));
      await AsyncStorage.setItem('aiRecommendations', JSON.stringify(recs));
    } catch { Alert.alert('Error', 'Could not run ATS analysis.'); }
    finally  { setAtsLoading(false); }
  }, [profileData, profileStats]);

  // ── Share ────────────────────────────────────────────────────────────────────
  const shareProfile = async () => {
    try {
      await Share.share({
        title:   `${profileData.personal.fullName} — Professional Profile`,
        message: `${profileData.personal.fullName} | ${profileData.personal.jobTitle} at ${profileData.personal.company}\n${profileData.personal.location}\nVia JobEng`,
      });
    } catch {}
  };

  // ── Derived ──────────────────────────────────────────────────────────────────
  const completion = clampPct(profileStats.completionPercentage || calcCompletion(profileData));
  const strength   = clampPct(profileStats.profileStrength     || calcStrength(profileData));
  // Use local URI for display (base64 data: URIs fail on Android RN Image)
  // Base64 is kept only for PDF export and API upload
  const profileImg = profileData.personal?.profileImage || profileData.personal?.profileImageB64 || null;
  const showImg    = isValidImageUri(profileImg);


  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER HELPERS
  // ─────────────────────────────────────────────────────────────────────────────
  const Field = ({ label, fieldPath, value, multiline, placeholder, icon }) => (
    <View style={st.fieldRow}>
      <Text style={st.fieldLabel}>{label}</Text>
      <TouchableOpacity style={st.fieldBtn} onPress={() => openEdit(fieldPath, value)}>
        {icon && <MaterialCommunityIcons name={icon} size={15} color="#94a3b8" style={{ marginRight: 6 }} />}
        <Text style={[st.fieldVal, !toStr(value).trim() && st.placeholder]} numberOfLines={multiline ? 4 : 1}>
          {toStr(value).trim() || placeholder || 'Tap to edit'}
        </Text>
        <Ionicons name="pencil" size={15} color="#94a3b8" />
      </TouchableOpacity>
    </View>
  );

  const SHdr = ({ title, subtitle, onAdd, icon }) => (
    <View style={st.sHdr}>
      <View style={st.sHdrL}>
        {icon && <MaterialCommunityIcons name={icon} size={18} color="#0e4e96" style={{ marginRight: 6 }} />}
        <View>
          <Text style={st.sTitle}>{title}</Text>
          {!!subtitle && <Text style={st.sSub}>{subtitle}</Text>}
        </View>
      </View>
      {onAdd && (
        <TouchableOpacity style={st.addBtn} onPress={onAdd}>
          <MaterialCommunityIcons name="plus" size={16} color="#0e4e96" />
          <Text style={st.addBtnT}>Add</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  const SBar = ({ label, value, color }) => {
    const pct = clampPct(value);
    const c   = color || scoreColor(pct);
    return (
      <View style={st.sbWrap}>
        <View style={st.sbRow}>
          <Text style={st.sbL}>{label}</Text>
          <Text style={[st.sbV, { color: c }]}>{pct}%</Text>
        </View>
        <View style={st.barTrack}>
          <View style={[st.barFill, { width: `${pct}%`, backgroundColor: c }]} />
        </View>
      </View>
    );
  };

  const Empty = ({ icon, title, sub }) => (
    <View style={st.empty}>
      <MaterialCommunityIcons name={icon} size={40} color="#cbd5e1" />
      <Text style={st.emptyT}>{title}</Text>
      {sub && <Text style={st.emptyS}>{sub}</Text>}
    </View>
  );

  const ICard = ({ children, onEdit, onDelete }) => (
    <View style={st.iCard}>
      <View style={st.iCardInner}>{children}</View>
      <View style={st.iActions}>
        <TouchableOpacity style={st.iActBtn} onPress={onEdit}>
          <Ionicons name="pencil" size={15} color="#0e4e96" />
          <Text style={{ fontSize: 11, color: '#0e4e96', fontWeight: '600', marginLeft: 3 }}>Edit</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[st.iActBtn, { borderColor: '#fecaca' }]} onPress={onDelete}>
          <Ionicons name="trash" size={15} color="#ef4444" />
          <Text style={{ fontSize: 11, color: '#ef4444', fontWeight: '600', marginLeft: 3 }}>Delete</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // TAB RENDERERS
  // ─────────────────────────────────────────────────────────────────────────────

  const renderPersonal = () => {
    const p = profileData.personal || {};
    return (
      <View style={st.card}>
        {/* Avatar row */}
        <View style={st.profileTop}>
          <TouchableOpacity style={st.avatarWrap} onPress={() => setPhotoModal(true)} disabled={photoUploading}>
            {photoUploading
              ? <View style={st.avatarPH}><ActivityIndicator size={24} color="#0e4e96" /></View>
              : showImg
                ? <Image source={{ uri: profileImg }} style={st.avatar} onError={() => {}} />
                : <View style={st.avatarPH}><Ionicons name="person" size={30} color="#94a3b8" /></View>
            }
            <View style={st.avatarBadge}><Ionicons name="camera" size={12} color="#FFF" /></View>
          </TouchableOpacity>
          <View style={{ flex: 1, marginLeft: 14 }}>
            <Text style={st.nameT}>{toStr(p.fullName) || 'Your Name'}</Text>
            <Text style={st.roleT}>{toStr(p.jobTitle) || 'Job Title'}{p.company ? ` · ${p.company}` : ''}</Text>
            <Text style={st.metaT}>{[p.location, p.email].filter(Boolean).join(' · ')}</Text>
            {/* Availability badge — tap to cycle */}
            <TouchableOpacity
              style={[st.availBadge,
                p.availability === 'Available'     ? { backgroundColor: '#DCFCE7', borderColor: '#86EFAC' } :
                p.availability === 'Open to Offers'? { backgroundColor: '#DBEAFE', borderColor: '#93C5FD' } :
                { backgroundColor: '#F1F5F9', borderColor: '#CBD5E1' }
              ]}
              onPress={() => {
                const i    = AVAILABILITIES.indexOf(p.availability || 'Available');
                const next = AVAILABILITIES[(i + 1) % AVAILABILITIES.length];
                setProfileData(prev => ({ ...prev, personal: { ...prev.personal, availability: next } }));
              }}
            >
              <View style={[st.availDot, {
                backgroundColor:
                  p.availability === 'Available'      ? '#16A34A' :
                  p.availability === 'Open to Offers' ? '#2563EB' : '#94A3B8',
              }]} />
              <Text style={[st.availT, {
                color: p.availability === 'Available'      ? '#16A34A' :
                       p.availability === 'Open to Offers' ? '#2563EB' : '#64748B',
              }]}>{p.availability || 'Available'}</Text>
            </TouchableOpacity>
          </View>
        </View>

        <View style={st.divider} />

        {/* AI About */}
        <TouchableOpacity style={st.aiAboutBtn} onPress={generateAbout}>
          <MaterialCommunityIcons name="robot" size={16} color="#8B5CF6" />
          <Text style={st.aiAboutT}>Generate About with AI</Text>
          {aiLoading && <ActivityIndicator size={12} color="#8B5CF6" style={{ marginLeft: 4 }} />}
        </TouchableOpacity>

        {/* Fields */}
        {[
          { label: 'Full Name',    path: 'personal.fullName',   val: p.fullName,   icon: 'account-outline'          },
          { label: 'Email',        path: 'personal.email',      val: p.email,      icon: 'email-outline'            },
          { label: 'Phone',        path: 'personal.phone',      val: p.phone,      icon: 'phone-outline'            },
          { label: 'Location',     path: 'personal.location',   val: p.location,   icon: 'map-marker-outline'       },
          { label: 'Job Title',    path: 'personal.jobTitle',   val: p.jobTitle,   icon: 'briefcase-outline'        },
          { label: 'Company',      path: 'personal.company',    val: p.company,    icon: 'office-building-outline'  },
          { label: 'Nationality',  path: 'personal.nationality',val: p.nationality,icon: 'flag-outline'             },
          { label: 'About',        path: 'personal.about',      val: p.about,      icon: 'text-box-outline',
            multiline: true, placeholder: 'Write 80–150 words about your expertise and achievements' },
          { label: 'LinkedIn',     path: 'personal.linkedin',   val: p.linkedin,   icon: 'linkedin'                },
          { label: 'GitHub',       path: 'personal.github',     val: p.github,     icon: 'github'                  },
          { label: 'Twitter / X',  path: 'personal.twitter',    val: p.twitter,    icon: 'twitter'                 },
          { label: 'Website',      path: 'personal.website',    val: p.website,    icon: 'web'                     },
        ].map(f => (
          <Field key={f.path} label={f.label} fieldPath={f.path} value={f.val}
            icon={f.icon} multiline={f.multiline} placeholder={f.placeholder} />
        ))}

        <View style={st.divider} />
        <Text style={[st.sTitle, { marginBottom: 10 }]}>Work Preferences</Text>

        {/* Availability chips */}
        <View style={st.fieldRow}>
          <Text style={st.fieldLabel}>Availability Status</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingVertical: 4 }}>
            {AVAILABILITIES.map(a => {
              const active = p.availability === a;
              return (
                <TouchableOpacity key={a} style={[st.pChip, active && st.pChipA]}
                  onPress={() => setProfileData(prev => ({ ...prev, personal: { ...prev.personal, availability: a } }))}>
                  <Text style={[st.pChipT, active && st.pChipTA]}>{a}</Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>

        {/* Notice period chips */}
        <View style={st.fieldRow}>
          <Text style={st.fieldLabel}>Notice Period</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingVertical: 4 }}>
            {NOTICE_PERIODS.map(n => {
              const active = p.noticePeriod === n;
              return (
                <TouchableOpacity key={n} style={[st.pChip, active && st.pChipA]}
                  onPress={() => setProfileData(prev => ({ ...prev, personal: { ...prev.personal, noticePeriod: n } }))}>
                  <Text style={[st.pChipT, active && st.pChipTA]}>{n}</Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      </View>
    );
  };

  const renderExperience = () => {
    const list = profileData.experience || [];
    return (
      <View style={st.card}>
        <SHdr title="Work Experience" subtitle="Most recent first · add metrics for best ATS results"
          icon="briefcase-outline" onAdd={() => openListModal('experience')} />
        {list.length === 0
          ? <Empty icon="briefcase-outline" title="No experience yet" sub='Tap "Add" to include your first role.' />
          : list.map(item => (
            <ICard key={item.id} onEdit={() => openListModal('experience', item)} onDelete={() => deleteItem('experience', item.id)}>
              <View style={st.iRow}>
                <View style={[st.iIcon, { backgroundColor: '#EFF6FF' }]}>
                  <MaterialCommunityIcons name="briefcase" size={16} color="#2563EB" />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={st.iTitle}>{toStr(item.title)}</Text>
                  <Text style={st.iMeta}>{toStr(item.company)}{item.location ? ` · ${item.location}` : ''}</Text>
                  <Text style={st.iDate}>
                    {[item.startDate, item.current ? 'Present' : item.endDate].filter(Boolean).join(' – ')}
                  </Text>
                </View>
              </View>
              {toStr(item.description).trim() && <Text style={st.iDesc}>{toStr(item.description)}</Text>}
              {toStr(item.achievements).trim() && (
                <View style={st.achRow}>
                  <MaterialCommunityIcons name="trophy-outline" size={13} color="#F59E0B" />
                  <Text style={st.achT}>{toStr(item.achievements)}</Text>
                </View>
              )}
            </ICard>
          ))
        }
      </View>
    );
  };

  const renderEducation = () => {
    const list = profileData.education || [];
    return (
      <View style={st.card}>
        <SHdr title="Education" subtitle="Degrees, diplomas & professional qualifications"
          icon="school-outline" onAdd={() => openListModal('education')} />
        {list.length === 0
          ? <Empty icon="school-outline" title="No education yet" sub='Tap "Add" to include your degree.' />
          : list.map(item => (
            <ICard key={item.id} onEdit={() => openListModal('education', item)} onDelete={() => deleteItem('education', item.id)}>
              <View style={st.iRow}>
                <View style={[st.iIcon, { backgroundColor: '#F0FDF4' }]}>
                  <MaterialCommunityIcons name="school" size={16} color="#16A34A" />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={st.iTitle}>{toStr(item.degree)}</Text>
                  <Text style={st.iMeta}>{toStr(item.institution)}{item.location ? ` · ${item.location}` : ''}</Text>
                  <Text style={st.iDate}>{[item.startDate, item.endDate].filter(Boolean).join(' – ')}</Text>
                  {item.gpa    && <Text style={st.iChip}>GPA: {item.gpa}</Text>}
                  {item.honors && <Text style={st.iChip}>🏅 {item.honors}</Text>}
                </View>
              </View>
              {toStr(item.description).trim() && <Text style={st.iDesc}>{toStr(item.description)}</Text>}
            </ICard>
          ))
        }
      </View>
    );
  };

  const renderSkills = () => {
    const raw  = profileData.skills || [];
    const list = raw.map(s => typeof s === 'string' ? { id: s, name: s, level: 'Intermediate' } : s).filter(Boolean);
    return (
      <View style={st.card}>
        <SHdr title="Skills" subtitle="Add 8–15 skills — technical & soft"
          icon="star-outline" onAdd={() => openListModal('skill')} />
        {list.length === 0
          ? <Empty icon="star-outline" title="No skills yet" sub='Tap "Add" to include skills.' />
          : list.map(sk => {
            const lc = SKILL_LEVEL_COLORS[sk.level] || '#94A3B8';
            const lp = SKILL_LEVEL_PCT[sk.level]    || 50;
            return (
              <View key={sk.id || sk.name} style={st.skRow}>
                <View style={{ flex: 1 }}>
                  <View style={st.skTR}>
                    <Text style={st.skName}>{toStr(sk.name)}</Text>
                    <Text style={[st.skLvl, { color: lc }]}>{sk.level || 'Intermediate'}</Text>
                  </View>
                  <View style={st.skBarT}>
                    <View style={[st.skBarF, { width: `${lp}%`, backgroundColor: lc }]} />
                  </View>
                </View>
                <View style={st.skActs}>
                  <TouchableOpacity onPress={() => openListModal('skill', sk)} style={st.skActBtn}>
                    <Ionicons name="pencil" size={13} color="#0e4e96" />
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => deleteItem('skills', sk.id || sk.name)} style={[st.skActBtn, { borderColor: '#fecaca' }]}>
                    <Ionicons name="close" size={13} color="#ef4444" />
                  </TouchableOpacity>
                </View>
              </View>
            );
          })
        }
        {list.length > 0 && (
          <Text style={st.helperT}>💡 Be specific — "SolidWorks 2023" beats "CAD software".</Text>
        )}
      </View>
    );
  };

  const renderCertifications = () => {
    const list = profileData.certifications || [];
    return (
      <View style={st.card}>
        <SHdr title="Certifications" subtitle="Credentials, licences & professional memberships"
          icon="certificate-outline" onAdd={() => openListModal('certification')} />
        {list.length === 0
          ? <Empty icon="certificate-outline" title="No certifications yet" sub='Tap "Add" to include a certificate.' />
          : list.map(item => (
            <ICard key={item.id} onEdit={() => openListModal('certification', item)} onDelete={() => deleteItem('certifications', item.id)}>
              <View style={st.iRow}>
                <View style={[st.iIcon, { backgroundColor: '#FFFBEB' }]}>
                  <MaterialCommunityIcons name="certificate" size={16} color="#D97706" />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={st.iTitle}>{toStr(item.name)}</Text>
                  <Text style={st.iMeta}>{toStr(item.issuer)}</Text>
                  <Text style={st.iDate}>{[item.date, item.expiry && `Exp: ${item.expiry}`].filter(Boolean).join(' · ')}</Text>
                  {item.credentialId && <Text style={st.iChip}>ID: {item.credentialId}</Text>}
                </View>
              </View>
            </ICard>
          ))
        }
      </View>
    );
  };

  const renderProjects = () => {
    const list = profileData.projects || [];
    return (
      <View style={st.card}>
        <SHdr title="Projects & Portfolio" subtitle="Showcase key work with tech stack and links"
          icon="code-tags" onAdd={() => openListModal('project')} />
        {list.length === 0
          ? <Empty icon="folder-open-outline" title="No projects yet" sub='Tap "Add" to showcase your work.' />
          : list.map(item => (
            <ICard key={item.id} onEdit={() => openListModal('project', item)} onDelete={() => deleteItem('projects', item.id)}>
              <View style={st.iRow}>
                <View style={[st.iIcon, { backgroundColor: '#F5F3FF' }]}>
                  <MaterialCommunityIcons name="code-braces" size={16} color="#7C3AED" />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={st.iTitle}>{toStr(item.name)}</Text>
                  {item.tech && <Text style={st.iMeta}>{item.tech}</Text>}
                  {item.url  && <Text style={[st.iChip, { color: '#2563EB' }]}>{item.url}</Text>}
                  <Text style={st.iDate}>{[item.startDate, item.endDate].filter(Boolean).join(' – ')}</Text>
                </View>
              </View>
              {toStr(item.description).trim() && <Text style={st.iDesc}>{toStr(item.description)}</Text>}
            </ICard>
          ))
        }
      </View>
    );
  };

  const renderLanguages = () => {
    const langs  = profileData.languages || [];
    const awards = profileData.awards    || [];
    const vol    = profileData.volunteer || [];
    const LC     = { Basic: '#94A3B8', Conversational: '#3B82F6', Professional: '#8B5CF6', Fluent: '#10B981', Native: '#F59E0B' };
    return (
      <>
        <View style={st.card}>
          <SHdr title="Languages" icon="chat-outline" onAdd={() => openListModal('language')} />
          {langs.length === 0
            ? <Empty icon="translate" title="No languages added" sub='Tap "Add" to include a language.' />
            : langs.map(item => (
              <ICard key={item.id} onEdit={() => openListModal('language', item)} onDelete={() => deleteItem('languages', item.id)}>
                <View style={st.iRow}>
                  <View style={[st.iIcon, { backgroundColor: '#F0FDF4' }]}>
                    <MaterialCommunityIcons name="web" size={16} color="#16A34A" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={st.iTitle}>{toStr(item.name)}</Text>
                    <Text style={[st.skLvl, { color: LC[item.level] || '#94A3B8', marginTop: 3 }]}>{item.level || 'Conversational'}</Text>
                  </View>
                </View>
              </ICard>
            ))
          }
        </View>

        <View style={st.card}>
          <SHdr title="Awards & Recognition" icon="trophy-outline" onAdd={() => openListModal('award')} />
          {awards.length === 0
            ? <Empty icon="trophy-outline" title="No awards yet" />
            : awards.map(item => (
              <ICard key={item.id} onEdit={() => openListModal('award', item)} onDelete={() => deleteItem('awards', item.id)}>
                <View style={st.iRow}>
                  <View style={[st.iIcon, { backgroundColor: '#FFFBEB' }]}>
                    <MaterialCommunityIcons name="trophy" size={16} color="#D97706" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={st.iTitle}>{toStr(item.title)}</Text>
                    <Text style={st.iMeta}>{toStr(item.issuer)}{item.date ? ` · ${item.date}` : ''}</Text>
                    {item.description && <Text style={st.iDesc}>{item.description}</Text>}
                  </View>
                </View>
              </ICard>
            ))
          }
        </View>

        <View style={st.card}>
          <SHdr title="Volunteer Work" icon="heart-outline" onAdd={() => openListModal('volunteer')} />
          {vol.length === 0
            ? <Empty icon="heart-outline" title="No volunteer work yet" />
            : vol.map(item => (
              <ICard key={item.id} onEdit={() => openListModal('volunteer', item)} onDelete={() => deleteItem('volunteer', item.id)}>
                <View style={st.iRow}>
                  <View style={[st.iIcon, { backgroundColor: '#FFF1F2' }]}>
                    <MaterialCommunityIcons name="heart" size={16} color="#E11D48" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={st.iTitle}>{toStr(item.role)}</Text>
                    <Text style={st.iMeta}>{toStr(item.org)}</Text>
                    <Text style={st.iDate}>{[item.startDate, item.endDate].filter(Boolean).join(' – ')}</Text>
                    {item.description && <Text style={st.iDesc}>{item.description}</Text>}
                  </View>
                </View>
              </ICard>
            ))
          }
        </View>
      </>
    );
  };

  const renderPreferences = () => {
    const prefs   = profileData.preferences || {};
    const privacy = profileData.privacy     || {};
    return (
      <>
        <View style={st.card}>
          <Text style={st.sTitle}>Job Preferences</Text>
          <View style={st.divider} />

          <View style={st.fieldRow}>
            <Text style={st.fieldLabel}>Job Types (select all that apply)</Text>
            <View style={st.chipsRow}>
              {JOB_TYPES_LIST.map(jt => {
                const active = (prefs.jobType || []).includes(jt);
                return (
                  <TouchableOpacity key={jt} style={[st.pChip, active && st.pChipA]}
                    onPress={() => setProfileData(prev => {
                      const cur = prev.preferences?.jobType || [];
                      return { ...prev, preferences: { ...prev.preferences, jobType: active ? cur.filter(x => x !== jt) : [...cur, jt] } };
                    })}>
                    <Text style={[st.pChipT, active && st.pChipTA]}>{jt}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          <View style={st.fieldRow}>
            <Text style={st.fieldLabel}>Preferred Work Location</Text>
            <View style={st.chipsRow}>
              {WORK_LOCS.map(wl => {
                const active = prefs.workLocation === wl;
                return (
                  <TouchableOpacity key={wl} style={[st.pChip, active && st.pChipA]}
                    onPress={() => setProfileData(prev => ({ ...prev, preferences: { ...prev.preferences, workLocation: wl } }))}>
                    <Text style={[st.pChipT, active && st.pChipTA]}>{wl}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          <Field label="Salary Expectation"   fieldPath="preferences.salaryRange"  value={prefs.salaryRange}  placeholder="e.g. £50k–£70k"       icon="currency-gbp" />
          <Field label="Industries of Interest" fieldPath="preferences.industries" value={(prefs.industries || []).join(', ')} placeholder="e.g. Civil, Energy, Tech" icon="domain" />
        </View>

        {/* Privacy */}
        <View style={st.card}>
          <Text style={st.sTitle}>Privacy Settings</Text>
          <View style={st.divider} />

          <View style={st.fieldRow}>
            <Text style={st.fieldLabel}>Profile Visibility</Text>
            <View style={st.chipsRow}>
              {PRIVACY_OPTS.map(opt => {
                const active = privacy.profileVisibility === opt;
                return (
                  <TouchableOpacity key={opt} style={[st.pChip, active && st.pChipA]}
                    onPress={() => setProfileData(prev => ({ ...prev, privacy: { ...prev.privacy, profileVisibility: opt } }))}>
                    <Text style={[st.pChipT, active && st.pChipTA]}>{opt}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          {[
            { k: 'showEmail',             l: 'Show Email to Recruiters'    },
            { k: 'showPhone',             l: 'Show Phone Number'           },
            { k: 'showSalary',            l: 'Show Salary Expectations'    },
            { k: 'showLocation',          l: 'Show Location'               },
            { k: 'allowRecruiterContact', l: 'Allow Recruiter Contact'     },
          ].map(opt => (
            <View key={opt.k} style={st.togRow}>
              <Text style={st.togL}>{opt.l}</Text>
              <Switch
                value={privacy[opt.k] !== false}
                onValueChange={v => setProfileData(prev => ({ ...prev, privacy: { ...prev.privacy, [opt.k]: v } }))}
                trackColor={{ false: '#E5E7EB', true: '#BFDBFE' }}
                thumbColor={privacy[opt.k] !== false ? '#0e4e96' : '#D1D5DB'}
              />
            </View>
          ))}
        </View>
      </>
    );
  };

  const renderAts = () => {
    const score = clampPct(atsResult?.score || 0);
    return (
      <View style={st.card}>
        <View style={st.sHdr}>
          <View style={st.sHdrL}>
            <MaterialCommunityIcons name="chart-line" size={18} color="#0e4e96" style={{ marginRight: 6 }} />
            <View>
              <Text style={st.sTitle}>ATS Compatibility Score</Text>
              <Text style={st.sSub}>How well your profile passes ATS screening</Text>
            </View>
          </View>
          <TouchableOpacity style={st.runBtn} onPress={runAts} disabled={atsLoading}>
            {atsLoading
              ? <ActivityIndicator size={14} color="#FFF" />
              : <><MaterialCommunityIcons name="play" size={14} color="#FFF" /><Text style={st.runBtnT}>Run</Text></>
            }
          </TouchableOpacity>
        </View>

        <View style={st.atsScoreRow}>
          <View style={[st.atsRing, { borderColor: scoreColor(score) }]}>
            <Text style={[st.atsV, { color: scoreColor(score) }]}>{score}</Text>
            <Text style={st.atsL}>/ 100</Text>
          </View>
          <View style={{ flex: 1, marginLeft: 16 }}>
            <SBar label={`Completion ${completion}%`} value={completion} />
            <SBar label={`Strength ${strength}%`}    value={strength}   />
            <SBar label={`ATS Score ${score}%`}      value={score}      />
          </View>
        </View>

        {atsResult?.keywords?.present?.length > 0 && (
          <View style={{ marginTop: 14 }}>
            <Text style={st.subT}>✅ Detected Keywords</Text>
            <View style={st.chipsW}>
              {atsResult.keywords.present.map((k, i) => (
                <View key={i} style={st.kwG}><Text style={st.kwGT}>{k}</Text></View>
              ))}
            </View>
          </View>
        )}
        {atsResult?.keywords?.missing?.length > 0 && (
          <View style={{ marginTop: 10 }}>
            <Text style={st.subT}>⚠️ Missing Keywords</Text>
            <View style={st.chipsW}>
              {atsResult.keywords.missing.map((k, i) => (
                <View key={i} style={st.kwR}><Text style={st.kwRT}>{k}</Text></View>
              ))}
            </View>
          </View>
        )}
        {atsResult?.notes?.length > 0 && (
          <View style={{ marginTop: 10 }}>
            <Text style={st.subT}>Notes</Text>
            {atsResult.notes.map((n, i) => (
              <View key={i} style={st.bRow}><View style={st.bDot} /><Text style={st.bT}>{n}</Text></View>
            ))}
          </View>
        )}
        {aiRecs.length > 0 && (
          <View style={{ marginTop: 10 }}>
            <Text style={st.subT}>🤖 AI Recommendations</Text>
            {aiRecs.map((r, i) => (
              <View key={i} style={[st.bRow, { alignItems: 'flex-start' }]}>
                <MaterialCommunityIcons name="lightbulb-outline" size={14} color="#F59E0B" style={{ marginTop: 2, marginRight: 8 }} />
                <Text style={st.bT}>{r}</Text>
              </View>
            ))}
          </View>
        )}

        <TouchableOpacity style={st.sharePBtn} onPress={shareProfile}>
          <Ionicons name="share-social-outline" size={16} color="#0e4e96" />
          <Text style={st.sharePT}>Share My Profile</Text>
        </TouchableOpacity>
      </View>
    );
  };


  // ─────────────────────────────────────────────────────────────────────────────
  // MODALS
  // ─────────────────────────────────────────────────────────────────────────────

  const renderEditModal = () => (
    <Modal visible={editModal} animationType="fade" transparent onRequestClose={() => setEditModal(false)}>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={[st.mOverlay, { justifyContent: 'center' }]}>
        <View style={[st.editCard, { margin: 20 }]}>
          <Text style={st.mTitle}>Edit {editField?.split('.')[1] || ''}</Text>
          <TextInput
            value={editValue}
            onChangeText={setEditValue}
            placeholder="Enter value"
            placeholderTextColor="#94a3b8"
            style={[st.mInput, editField?.endsWith('.about') && st.mInputM]}
            multiline={editField?.endsWith('.about')}
            autoFocus
          />
          <View style={st.mFooter}>
            <TouchableOpacity style={st.mGhost}   onPress={() => setEditModal(false)}>
              <Text style={st.mGhostT}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={st.mPrimary} onPress={applyEdit}>
              <Text style={st.mPrimaryT}>Save</Text>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );

  const renderListModal = () => {
    const type = listType;
    const lfP = { listForm, setListForm };
    const TITLES = {
      experience: 'Work Experience', education: 'Education', certification: 'Certification',
      skill: 'Skill', project: 'Project', language: 'Language', award: 'Award', volunteer: 'Volunteer Work',
    };

    return (
      <Modal visible={listModal} animationType="slide" transparent onRequestClose={closeListModal}>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={st.mOverlay}>
          <View style={[st.mSheet, { maxHeight: height * 0.92 }]}>
            <View style={st.mHdr}>
              <Text style={st.mTitle}>{listEditId ? 'Edit' : 'Add'} {TITLES[type] || ''}</Text>
              <TouchableOpacity onPress={closeListModal}>
                <Ionicons name="close" size={22} color="#64748b" />
              </TouchableOpacity>
            </View>
            <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 20 }}>

              {type === 'experience' && (<>
                <LF {...lfP} label="Job Title"    fkey="title"        placeholder="e.g. Mechanical Engineer"       required />
                <LF {...lfP} label="Company"      fkey="company"      placeholder="e.g. ABC Engineering Ltd"       required />
                <LF {...lfP} label="Location"     fkey="location"     placeholder="e.g. London, UK"                        />
                <View style={{ flexDirection: 'row', gap: 12 }}>
                  <View style={{ flex: 1 }}><LF {...lfP} label="Start Date" fkey="startDate" placeholder="Jan 2020" /></View>
                  <View style={{ flex: 1 }}>
                    <Text style={st.mLabel}>End Date</Text>
                    <TextInput
                      value={listForm.current ? 'Present' : toStr(listForm.endDate)}
                      onChangeText={v => setListForm(p => ({ ...p, endDate: v, current: false }))}
                      placeholder="Present" placeholderTextColor="#94a3b8" style={st.mInput}
                    />
                  </View>
                </View>
                <View style={st.togRow}>
                  <Text style={st.togL}>I currently work here</Text>
                  <Switch
                    value={!!listForm.current}
                    onValueChange={v => setListForm(p => ({ ...p, current: v, endDate: v ? 'Present' : p.endDate }))}
                    trackColor={{ false: '#E5E7EB', true: '#BFDBFE' }}
                    thumbColor={listForm.current ? '#0e4e96' : '#D1D5DB'}
                  />
                </View>
                <LF {...lfP} label="Description"      fkey="description"  placeholder="Key responsibilities, technologies used…" multiline />
                <LF {...lfP} label="Key Achievements" fkey="achievements"
                  placeholder={'• Reduced cost by 15%\n• Led team of 8 engineers\n• Delivered project 2 weeks early'} multiline />
              </>)}

              {type === 'education' && (<>
                <LF {...lfP} label="Degree / Qualification" fkey="degree"       placeholder="e.g. BEng Mechanical Engineering"  required />
                <LF {...lfP} label="Institution"            fkey="institution"  placeholder="e.g. University of Manchester"      required />
                <LF {...lfP} label="Location"               fkey="location"     placeholder="Manchester, UK"                              />
                <View style={{ flexDirection: 'row', gap: 12 }}>
                  <View style={{ flex: 1 }}><LF {...lfP} label="Start" fkey="startDate" placeholder="2018" /></View>
                  <View style={{ flex: 1 }}><LF {...lfP} label="End"   fkey="endDate"   placeholder="2022" /></View>
                </View>
                <LF {...lfP} label="GPA / Grade"      fkey="gpa"         placeholder="e.g. 3.8 / First Class"           />
                <LF {...lfP} label="Honors / Awards"  fkey="honors"      placeholder="e.g. Dean's List"                  />
                <LF {...lfP} label="Relevant Modules" fkey="description" placeholder="Thermodynamics, FEA, Project Mgmt…" multiline />
              </>)}

              {type === 'certification' && (<>
                <LF {...lfP} label="Certificate Name" fkey="name"          placeholder="e.g. NEBOSH General Certificate"  required />
                <LF {...lfP} label="Issuer"           fkey="issuer"        placeholder="e.g. NEBOSH"                      required />
                <View style={{ flexDirection: 'row', gap: 12 }}>
                  <View style={{ flex: 1 }}><LF {...lfP} label="Issue Date" fkey="date"   placeholder="2024"          /></View>
                  <View style={{ flex: 1 }}><LF {...lfP} label="Expiry"     fkey="expiry" placeholder="2027 / None"    /></View>
                </View>
                <LF {...lfP} label="Credential ID"   fkey="credentialId" placeholder="e.g. ABC-123456"                  />
                <LF {...lfP} label="Certificate URL" fkey="url"          placeholder="https://…"                         />
              </>)}

              {type === 'skill' && (<>
                <LF {...lfP} label="Skill Name" fkey="name" placeholder="e.g. AutoCAD, Python, Agile" required />
                <Text style={st.mLabel}>Proficiency Level</Text>
                <View style={st.chipsRow}>
                  {SKILL_LEVELS.map(l => (
                    <TouchableOpacity key={l} style={[st.pChip, listForm.level === l && st.pChipA]}
                      onPress={() => setListForm(p => ({ ...p, level: l }))}>
                      <Text style={[st.pChipT, listForm.level === l && st.pChipTA]}>{l}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </>)}

              {type === 'project' && (<>
                <LF {...lfP} label="Project Name"  fkey="name"        placeholder="e.g. Smart Energy Monitor" required />
                <LF {...lfP} label="Technologies"  fkey="tech"        placeholder="e.g. Python, React, PostgreSQL"      />
                <LF {...lfP} label="Project URL"   fkey="url"         placeholder="https://github.com/…"               />
                <View style={{ flexDirection: 'row', gap: 12 }}>
                  <View style={{ flex: 1 }}><LF {...lfP} label="Start" fkey="startDate" placeholder="Jan 2023" /></View>
                  <View style={{ flex: 1 }}><LF {...lfP} label="End"   fkey="endDate"   placeholder="Jun 2023" /></View>
                </View>
                <LF {...lfP} label="Description" fkey="description" placeholder="What it does and your role…" multiline />
              </>)}

              {type === 'language' && (<>
                <LF {...lfP} label="Language" fkey="name" placeholder="e.g. French, Arabic" required />
                <Text style={st.mLabel}>Proficiency Level</Text>
                <View style={st.chipsRow}>
                  {LANG_LEVELS.map(l => (
                    <TouchableOpacity key={l} style={[st.pChip, listForm.level === l && st.pChipA]}
                      onPress={() => setListForm(p => ({ ...p, level: l }))}>
                      <Text style={[st.pChipT, listForm.level === l && st.pChipTA]}>{l}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </>)}

              {type === 'award' && (<>
                <LF {...lfP} label="Award Title" fkey="title"       placeholder="e.g. Engineer of the Year"           required />
                <LF {...lfP} label="Issuer"      fkey="issuer"      placeholder="e.g. Institution of Engineering"     required />
                <LF {...lfP} label="Date"        fkey="date"        placeholder="2023"                                         />
                <LF {...lfP} label="Description" fkey="description" placeholder="Brief context about the award…"      multiline />
              </>)}

              {type === 'volunteer' && (<>
                <LF {...lfP} label="Role"         fkey="role"        placeholder="e.g. Volunteer Engineer"       required />
                <LF {...lfP} label="Organisation" fkey="org"         placeholder="e.g. Engineers Without Borders" required />
                <View style={{ flexDirection: 'row', gap: 12 }}>
                  <View style={{ flex: 1 }}><LF {...lfP} label="Start" fkey="startDate" placeholder="2021"    /></View>
                  <View style={{ flex: 1 }}><LF {...lfP} label="End"   fkey="endDate"   placeholder="Present" /></View>
                </View>
                <LF {...lfP} label="Description" fkey="description" placeholder="What you did and the impact…" multiline />
              </>)}

            </ScrollView>
            <View style={st.mFooter}>
              <TouchableOpacity style={st.mGhost}   onPress={closeListModal}><Text style={st.mGhostT}>Cancel</Text></TouchableOpacity>
              <TouchableOpacity style={st.mPrimary} onPress={upsertListItem}><Text style={st.mPrimaryT}>Save</Text></TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    );
  };

  const renderPhotoModal = () => (
    <Modal
      visible={photoModal}
      animationType="slide"
      transparent
      onRequestClose={() => setPhotoModal(false)}
    >
      {/*
        IMPORTANT: backdrop and card are SIBLINGS, not nested.
        If the card were inside the backdrop TouchableOpacity, tapping any button
        inside the card would also fire the backdrop's onPress, closing the modal
        before the picker opens — especially on Android.
      */}
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' }}>

        {/* Backdrop — tap outside card to dismiss */}
        <TouchableOpacity
          style={StyleSheet.absoluteFill}
          activeOpacity={1}
          onPress={() => setPhotoModal(false)}
        />

        {/* Card — sits on top of backdrop, touches don't propagate to backdrop */}
        <View style={[st.photoCard, { pointerEvents: 'box-none' }]}>
          <View style={st.photoCardHandle} />

          <Text style={st.photoCardTitle}>Profile Photo</Text>
          <Text style={st.photoCardSub}>Square crop · JPG or PNG · max 5 MB</Text>

          <TouchableOpacity
            style={st.photoOptionRow}
            onPress={() => pickPhoto('camera')}
            activeOpacity={0.7}
          >
            <View style={[st.photoOptionIcon, { backgroundColor: '#EFF6FF' }]}>
              <Ionicons name="camera" size={20} color="#0e4e96" />
            </View>
            <Text style={st.photoOptionText}>Take a photo</Text>
            <Ionicons name="chevron-forward" size={18} color="#CBD5E1" />
          </TouchableOpacity>

          <TouchableOpacity
            style={st.photoOptionRow}
            onPress={() => pickPhoto('gallery')}
            activeOpacity={0.7}
          >
            <View style={[st.photoOptionIcon, { backgroundColor: '#F0FDF4' }]}>
              <Ionicons name="images" size={20} color="#16A34A" />
            </View>
            <Text style={st.photoOptionText}>Choose from gallery</Text>
            <Ionicons name="chevron-forward" size={18} color="#CBD5E1" />
          </TouchableOpacity>

          <TouchableOpacity
            style={st.photoOptionRow}
            onPress={useResumePhoto}
            activeOpacity={0.7}
          >
            <View style={[st.photoOptionIcon, { backgroundColor: '#FEF3C7' }]}>
              <Ionicons name="document-text" size={20} color="#fdb913" />
            </View>
            <Text style={st.photoOptionText}>Use photo from resume</Text>
            <Ionicons name="chevron-forward" size={18} color="#CBD5E1" />
          </TouchableOpacity>

          <TouchableOpacity
            style={[st.photoOptionRow, { borderBottomWidth: 0 }]}
            onPress={removePhoto}
            activeOpacity={0.7}
          >
            <View style={[st.photoOptionIcon, { backgroundColor: '#FEF2F2' }]}>
              <Ionicons name="trash" size={20} color="#EF4444" />
            </View>
            <Text style={[st.photoOptionText, { color: '#EF4444' }]}>Remove photo</Text>
            <Ionicons name="chevron-forward" size={18} color="#CBD5E1" />
          </TouchableOpacity>

          {!ImagePicker && (
            <Text style={{ color: '#94a3b8', fontSize: 11, marginTop: 12, textAlign: 'center' }}>
              Run: npx expo install expo-image-picker
            </Text>
          )}

          <TouchableOpacity
            style={st.photoCardCancel}
            onPress={() => setPhotoModal(false)}
            activeOpacity={0.7}
          >
            <Text style={st.photoCardCancelText}>Cancel</Text>
          </TouchableOpacity>
        </View>

      </View>
    </Modal>
  );

  const renderPreviewModal = () => {
    const p = profileData.personal || {};
    return (
      <Modal visible={previewModal} animationType="slide" transparent onRequestClose={() => setPreviewModal(false)}>
        <View style={st.mOverlay}>
          <View style={[st.mSheet, { maxHeight: height * 0.92 }]}>
            <View style={st.mHdr}>
              <Text style={st.mTitle}>Profile Preview</Text>
              <TouchableOpacity onPress={() => setPreviewModal(false)}>
                <Ionicons name="close" size={22} color="#64748b" />
              </TouchableOpacity>
            </View>
            <ScrollView contentContainerStyle={{ padding: 20, paddingBottom: 40 }}>
              <View style={st.prvHero}>
                {showImg
                  ? <Image source={{ uri: profileImg }} style={st.prvAvatar} onError={() => {}} />
                  : <View style={[st.prvAvatar, { backgroundColor: '#EFF6FF', justifyContent: 'center', alignItems: 'center' }]}>
                      <Text style={{ fontSize: 36, fontWeight: '900', color: '#0e4e96' }}>{p.fullName?.charAt(0) || '?'}</Text>
                    </View>
                }
                <Text style={st.prvName}>{p.fullName || 'Your Name'}</Text>
                <Text style={st.prvRole}>{p.jobTitle}{p.company ? ` · ${p.company}` : ''}</Text>
                <Text style={st.prvMeta}>{[p.location, p.email].filter(Boolean).join(' · ')}</Text>
                {p.about && <Text style={st.prvAbout}>{p.about}</Text>}
              </View>

              {profileData.skills?.length > 0 && (
                <View style={{ marginTop: 16 }}>
                  <Text style={[st.subT, { marginBottom: 8 }]}>Skills</Text>
                  <View style={st.chipsW}>
                    {(profileData.skills || []).slice(0, 12).map((sk, i) => (
                      <View key={i} style={st.prvSkChip}>
                        <Text style={st.prvSkT}>{sk.name || sk}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {profileData.experience?.length > 0 && (
                <View style={{ marginTop: 16 }}>
                  <Text style={[st.subT, { marginBottom: 8 }]}>Experience</Text>
                  {profileData.experience.slice(0, 3).map((e, i) => (
                    <View key={i} style={st.prvItem}>
                      <Text style={st.prvItemT}>{e.title}</Text>
                      <Text style={st.prvItemM}>
                        {e.company}{e.startDate ? ` · ${e.startDate}–${e.current ? 'Present' : e.endDate}` : ''}
                      </Text>
                    </View>
                  ))}
                </View>
              )}

              {profileData.education?.length > 0 && (
                <View style={{ marginTop: 16 }}>
                  <Text style={[st.subT, { marginBottom: 8 }]}>Education</Text>
                  {profileData.education.slice(0, 2).map((e, i) => (
                    <View key={i} style={st.prvItem}>
                      <Text style={st.prvItemT}>{e.degree}</Text>
                      <Text style={st.prvItemM}>{e.institution}{e.gpa ? ` · GPA: ${e.gpa}` : ''}</Text>
                    </View>
                  ))}
                </View>
              )}
            </ScrollView>
            <View style={st.mFooter}>
              <TouchableOpacity style={st.mGhost} onPress={() => setPreviewModal(false)}>
                <Text style={st.mGhostT}>Close</Text>
              </TouchableOpacity>
              <TouchableOpacity style={st.mPrimary} onPress={shareProfile}>
                <Ionicons name="share-social-outline" size={16} color="#FFF" />
                <Text style={[st.mPrimaryT, { marginLeft: 6 }]}>Share</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  const renderAiModal = () => (
    <Modal visible={aiModal} animationType="slide" transparent onRequestClose={() => setAiModal(false)}>
      <View style={st.mOverlay}>
        <View style={[st.mSheet, { maxHeight: height * 0.75 }]}>
          <View style={st.mHdr}>
            <Text style={st.mTitle}>🤖 AI About Suggestions</Text>
            <TouchableOpacity onPress={() => setAiModal(false)}>
              <Ionicons name="close" size={22} color="#64748b" />
            </TouchableOpacity>
          </View>
          {aiLoading
            ? <View style={{ padding: 40, alignItems: 'center' }}>
                <ActivityIndicator size={36} color="#8B5CF6" />
                <Text style={{ marginTop: 12, color: '#64748b' }}>Generating personalised suggestions…</Text>
              </View>
            : <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 30 }}>
                <Text style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>
                  Tap a suggestion to use it as your About section:
                </Text>
                {aiSuggestions.map((sug, i) => (
                  <TouchableOpacity key={i} style={st.aiSugItem} onPress={() => {
                    setProfileData(prev => ({ ...prev, personal: { ...prev.personal, about: sug } }));
                    setAiModal(false);
                    Alert.alert('✅ Applied', 'About section updated. Tap Save to keep it.');
                  }}>
                    <Text style={st.aiSugText}>{sug}</Text>
                    <Text style={st.aiSugApply}>Tap to apply →</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
          }
        </View>
      </View>
    </Modal>
  );

  // ─── Route tab content ────────────────────────────────────────────────────
  const renderTabContent = () => {
    if (activeTab === 'personal')       return renderPersonal();
    if (activeTab === 'experience')     return renderExperience();
    if (activeTab === 'education')      return renderEducation();
    if (activeTab === 'skills')         return renderSkills();
    if (activeTab === 'certifications') return renderCertifications();
    if (activeTab === 'projects')       return renderProjects();
    if (activeTab === 'languages')      return renderLanguages();
    if (activeTab === 'preferences')    return renderPreferences();
    if (activeTab === 'ats')            return renderAts();
    return renderPersonal();
  };

  // ─── Main render ──────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={st.root}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Top header */}
      <View style={st.topHdr}>
        <TouchableOpacity style={st.hdrIcon} onPress={() => {
          if (route?.params?.fromDashboard || navigation.canGoBack()) navigation.goBack();
          else navigation.navigate('ProfessionalDashboard');
        }}>
          <Ionicons name="arrow-back" size={20} color="#FFF" />
        </TouchableOpacity>

        <Text style={st.topTitle}>My Profile</Text>

        <View style={{ flexDirection: 'row', gap: 8 }}>
          <TouchableOpacity style={st.hdrIcon} onPress={() => setPreviewModal(true)}>
            <Ionicons name="eye-outline" size={18} color="#FFF" />
          </TouchableOpacity>
          <TouchableOpacity style={st.hdrIcon} onPress={shareProfile}>
            <Ionicons name="share-social-outline" size={18} color="#FFF" />
          </TouchableOpacity>
          <TouchableOpacity style={[st.hdrIcon, saving && { opacity: 0.6 }]}
            onPress={() => saveAll(profileData, true)} disabled={saving}>
            {saving
              ? <ActivityIndicator size={14} color="#FFF" />
              : <MaterialCommunityIcons name="content-save" size={18} color={hasUnsaved ? '#FCD34D' : '#FFF'} />
            }
          </TouchableOpacity>
        </View>
      </View>

      {/* Progress bars */}
      <View style={st.statsHdr}>
        <SBar label={`Completion ${completion}%`} value={completion} />
        <SBar label={`Strength ${strength}%`}     value={strength}   />
        {hasUnsaved && <Text style={st.unsavedH}>● Unsaved changes — auto-saves in 2s</Text>}
      </View>

      {/* Tab bar */}
      <View style={st.tabBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={st.tabBarC}>
          {tabs.map(t => (
            <TouchableOpacity key={t.key} style={[st.tabBtn, activeTab === t.key && st.tabBtnA]}
              onPress={() => setActiveTab(t.key)}>
              <Ionicons name={t.icon} size={13} color={activeTab === t.key ? '#0e4e96' : '#64748B'} />
              <Text style={[st.tabT, activeTab === t.key && st.tabTA]}>{t.title}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Content */}
      <Animated.ScrollView
        style={[st.scroll, { opacity: fadeAnim }]}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']} />}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ paddingBottom: 60 }}
      >
        {renderTabContent()}
      </Animated.ScrollView>

      {renderEditModal()}
      {renderListModal()}
      {renderPhotoModal()}
      {renderPreviewModal()}
      {renderAiModal()}
    </SafeAreaView>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const st = StyleSheet.create({
  root:         { flex: 1, backgroundColor: '#0e4e96' },
  topHdr:       { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 10, backgroundColor: '#0e4e96' },
  hdrIcon:      { width: 36, height: 36, borderRadius: 10, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.15)' },
  topTitle:     { flex: 1, textAlign: 'center', color: '#FFF', fontSize: 18, fontWeight: '700' },
  statsHdr:     { backgroundColor: '#0e4e96', paddingHorizontal: 14, paddingBottom: 12 },
  sbWrap:       { marginTop: 8 },
  sbRow:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  sbL:          { color: 'rgba(255,255,255,0.8)', fontSize: 11, fontWeight: '600' },
  sbV:          { fontSize: 11, fontWeight: '800' },
  barTrack:     { height: 7, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.2)', marginTop: 5, overflow: 'hidden' },
  barFill:      { height: 7, borderRadius: 8 },
  unsavedH:     { color: '#FCD34D', fontSize: 11, marginTop: 6 },
  tabBar:       { backgroundColor: '#F8FAFC', borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  tabBarC:      { paddingHorizontal: 10, paddingVertical: 8 },
  tabBtn:       { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 11, paddingVertical: 7, borderRadius: 14, backgroundColor: '#FFF', marginRight: 8, borderWidth: 1, borderColor: '#E2E8F0', gap: 4 },
  tabBtnA:      { borderColor: '#0e4e96', backgroundColor: '#EFF6FF' },
  tabT:         { fontSize: 11, color: '#64748B', fontWeight: '700' },
  tabTA:        { color: '#0e4e96' },
  scroll:       { flex: 1, backgroundColor: '#F8FAFC' },
  card:         { backgroundColor: '#FFF', marginHorizontal: 14, marginTop: 14, borderRadius: 18, padding: 14, borderWidth: 1, borderColor: '#E2E8F0', elevation: 2, shadowColor: '#000', shadowOpacity: 0.05, shadowRadius: 8, shadowOffset: { width: 0, height: 3 } },
  divider:      { height: 1, backgroundColor: '#E2E8F0', marginVertical: 12 },
  // Profile top
  profileTop:   { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  avatarWrap:   { width: 76, height: 76, position: 'relative' },
  avatar:       { width: 76, height: 76, borderRadius: 20, backgroundColor: '#F1F5F9' },
  avatarPH:     { width: 76, height: 76, borderRadius: 20, backgroundColor: '#F1F5F9', borderWidth: 1, borderColor: '#E2E8F0', justifyContent: 'center', alignItems: 'center' },
  avatarBadge:  { position: 'absolute', right: -3, bottom: -3, width: 26, height: 26, borderRadius: 10, backgroundColor: '#0e4e96', justifyContent: 'center', alignItems: 'center', borderWidth: 2, borderColor: '#FFF' },
  nameT:        { color: '#0F172A', fontSize: 17, fontWeight: '900' },
  roleT:        { color: '#334155', fontSize: 13, fontWeight: '700', marginTop: 2 },
  metaT:        { color: '#64748B', fontSize: 12, marginTop: 3 },
  availBadge:   { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 20, borderWidth: 1, marginTop: 6, gap: 5 },
  availDot:     { width: 7, height: 7, borderRadius: 4 },
  availT:       { fontSize: 11, fontWeight: '700' },
  aiAboutBtn:   { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#F5F3FF', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8, marginBottom: 14, borderWidth: 1, borderColor: '#DDD6FE' },
  aiAboutT:     { fontSize: 13, fontWeight: '600', color: '#7C3AED' },
  // Fields
  fieldRow:     { marginBottom: 10 },
  fieldLabel:   { color: '#0F172A', fontSize: 12, fontWeight: '800', marginBottom: 5 },
  fieldBtn:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderRadius: 12, paddingHorizontal: 12, paddingVertical: 12, backgroundColor: '#F8FAFC', borderWidth: 1, borderColor: '#E2E8F0', gap: 8 },
  fieldVal:     { flex: 1, color: '#0F172A', fontSize: 13, fontWeight: '600' },
  placeholder:  { color: '#94A3B8', fontWeight: '500' },
  // Section header
  sHdr:         { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  sHdrL:        { flex: 1, flexDirection: 'row', alignItems: 'center' },
  sTitle:       { color: '#0F172A', fontSize: 16, fontWeight: '900' },
  sSub:         { color: '#64748B', fontSize: 11, marginTop: 2 },
  addBtn:       { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 12, paddingVertical: 7, borderRadius: 14, backgroundColor: '#EFF6FF', borderWidth: 1, borderColor: '#BFDBFE' },
  addBtnT:      { color: '#0e4e96', fontWeight: '900', fontSize: 12 },
  // Item cards
  iCard:        { borderRadius: 14, borderWidth: 1, borderColor: '#E2E8F0', marginTop: 10, overflow: 'hidden' },
  iCardInner:   { padding: 12 },
  iActions:     { flexDirection: 'row', borderTopWidth: 1, borderTopColor: '#F1F5F9' },
  iActBtn:      { flex: 1, paddingVertical: 9, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', borderRightWidth: 1, borderColor: '#F1F5F9', gap: 4 },
  iRow:         { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  iIcon:        { width: 36, height: 36, borderRadius: 10, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  iTitle:       { color: '#0F172A', fontSize: 14, fontWeight: '900' },
  iMeta:        { color: '#3B82F6', fontSize: 12, fontWeight: '700', marginTop: 2 },
  iDate:        { color: '#64748B', fontSize: 11, marginTop: 2 },
  iChip:        { fontSize: 11, color: '#64748B', marginTop: 3, fontStyle: 'italic' },
  iDesc:        { color: '#334155', fontSize: 12, marginTop: 8, lineHeight: 18 },
  achRow:       { flexDirection: 'row', alignItems: 'flex-start', gap: 6, marginTop: 6, backgroundColor: '#FFFBEB', borderRadius: 8, padding: 8 },
  achT:         { flex: 1, fontSize: 12, color: '#92400E', lineHeight: 18 },
  // Empty
  empty:        { alignItems: 'center', paddingVertical: 22 },
  emptyT:       { marginTop: 10, color: '#0F172A', fontSize: 14, fontWeight: '900' },
  emptyS:       { marginTop: 5, color: '#64748B', fontSize: 12, textAlign: 'center' },
  // Skills
  skRow:        { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F1F5F9', gap: 10 },
  skTR:         { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 },
  skName:       { fontSize: 14, fontWeight: '700', color: '#0F172A' },
  skLvl:        { fontSize: 11, fontWeight: '700' },
  skBarT:       { height: 5, backgroundColor: '#E5E7EB', borderRadius: 3, overflow: 'hidden' },
  skBarF:       { height: 5, borderRadius: 3 },
  skActs:       { flexDirection: 'row', gap: 6 },
  skActBtn:     { width: 30, height: 30, borderRadius: 8, justifyContent: 'center', alignItems: 'center', borderWidth: 1, borderColor: '#E2E8F0', backgroundColor: '#F8FAFC' },
  helperT:      { marginTop: 12, color: '#64748B', fontSize: 12, lineHeight: 18 },
  // Chips / prefs
  chipsW:       { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  chipsRow:     { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 6 },
  pChip:        { borderRadius: 20, borderWidth: 1, borderColor: '#E2E8F0', paddingHorizontal: 12, paddingVertical: 7, backgroundColor: '#F8FAFC' },
  pChipA:       { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  pChipT:       { fontSize: 12, color: '#374151', fontWeight: '500' },
  pChipTA:      { color: '#FFF' },
  // Toggle
  togRow:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  togL:         { fontSize: 14, color: '#374151', fontWeight: '500', flex: 1 },
  // ATS
  atsScoreRow:  { flexDirection: 'row', alignItems: 'center', marginVertical: 14 },
  atsRing:      { width: 80, height: 80, borderRadius: 40, borderWidth: 5, justifyContent: 'center', alignItems: 'center' },
  atsV:         { fontSize: 24, fontWeight: '900' },
  atsL:         { fontSize: 10, color: '#94A3B8' },
  subT:         { fontSize: 14, fontWeight: '800', color: '#0F172A', marginBottom: 6 },
  bRow:         { flexDirection: 'row', alignItems: 'center', marginTop: 6, gap: 8 },
  bDot:         { width: 7, height: 7, borderRadius: 4, backgroundColor: '#0e4e96', flexShrink: 0 },
  bT:           { flex: 1, color: '#334155', fontSize: 12, lineHeight: 18 },
  kwG:          { backgroundColor: '#F0FDF4', borderRadius: 10, paddingHorizontal: 10, paddingVertical: 4, borderWidth: 1, borderColor: '#86EFAC' },
  kwGT:         { fontSize: 11, color: '#16A34A', fontWeight: '600' },
  kwR:          { backgroundColor: '#FEF2F2', borderRadius: 10, paddingHorizontal: 10, paddingVertical: 4, borderWidth: 1, borderColor: '#FCA5A5' },
  kwRT:         { fontSize: 11, color: '#DC2626', fontWeight: '600' },
  runBtn:       { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 12, backgroundColor: '#0e4e96' },
  runBtnT:      { color: '#FFF', fontSize: 12, fontWeight: '900' },
  sharePBtn:    { flexDirection: 'row', alignItems: 'center', gap: 8, justifyContent: 'center', marginTop: 16, paddingVertical: 11, borderRadius: 12, borderWidth: 1, borderColor: '#BFDBFE', backgroundColor: '#EFF6FF' },
  sharePT:      { color: '#0e4e96', fontSize: 14, fontWeight: '700' },
  // Modals
  mOverlay:     { flex: 1, backgroundColor: 'rgba(15,23,42,0.55)', justifyContent: 'flex-end' },
  mSheet:       { backgroundColor: '#FFF', borderTopLeftRadius: 24, borderTopRightRadius: 24, overflow: 'hidden' },
  mHdr:         { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 18, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  mTitle:       { color: '#0F172A', fontSize: 16, fontWeight: '900' },
  mLabel:       { color: '#0F172A', fontSize: 12, fontWeight: '800', marginBottom: 6 },
  mInput:       { borderWidth: 1, borderColor: '#E2E8F0', backgroundColor: '#F8FAFC', borderRadius: 12, paddingHorizontal: 12, paddingVertical: 12, color: '#0F172A', fontSize: 13, fontWeight: '600' },
  mInputM:      { minHeight: 100, textAlignVertical: 'top' },
  mFooter:      { flexDirection: 'row', gap: 10, padding: 14, borderTopWidth: 1, borderTopColor: '#E2E8F0' },
  mGhost:       { flex: 1, paddingVertical: 12, borderRadius: 12, backgroundColor: '#F1F5F9', borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center', justifyContent: 'center', flexDirection: 'row' },
  mGhostT:      { fontSize: 14, fontWeight: '700', color: '#0F172A' },
  mPrimary:     { flex: 2, paddingVertical: 12, borderRadius: 12, backgroundColor: '#0e4e96', alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 6 },
  mPrimaryT:    { fontSize: 14, fontWeight: '700', color: '#FFF' },
  editCard:     { backgroundColor: '#FFF', borderRadius: 18, padding: 16, borderWidth: 1, borderColor: '#E2E8F0' },
  photoRow:     { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 13, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  photoRowD:    { borderBottomWidth: 0, marginTop: 4 },
  photoRowT:    { fontSize: 14, fontWeight: '700', color: '#0F172A' },
  // Preview
  prvHero:      { alignItems: 'center', paddingVertical: 20 },
  prvAvatar:    { width: 90, height: 90, borderRadius: 20, marginBottom: 12 },
  prvName:      { fontSize: 22, fontWeight: '900', color: '#0F172A', textAlign: 'center' },
  prvRole:      { fontSize: 15, color: '#0e4e96', fontWeight: '700', marginTop: 3 },
  prvMeta:      { fontSize: 13, color: '#64748B', marginTop: 4 },
  prvAbout:     { fontSize: 13, color: '#334155', lineHeight: 20, marginTop: 12, textAlign: 'center' },
  prvItem:      { paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  prvItemT:     { fontSize: 14, fontWeight: '700', color: '#0F172A' },
  prvItemM:     { fontSize: 12, color: '#64748B', marginTop: 2 },
  prvSkChip:    { backgroundColor: '#EFF6FF', borderRadius: 14, paddingHorizontal: 10, paddingVertical: 5 },
  prvSkT:       { fontSize: 12, color: '#1D4ED8', fontWeight: '600' },
  // AI suggestions
  aiSugItem:    { backgroundColor: '#F8FAFC', borderRadius: 12, padding: 14, borderWidth: 1, borderColor: '#E2E8F0', marginBottom: 10 },
  aiSugText:    { fontSize: 14, color: '#374151', lineHeight: 22 },
  aiSugApply:   { fontSize: 12, color: '#7C3AED', fontWeight: '700', marginTop: 8, textAlign: 'right' },
  // Photo modal (bottom sheet style)
  photoCard:         { backgroundColor:'#FFF', borderTopLeftRadius:24, borderTopRightRadius:24, padding:20, paddingBottom:Platform.OS==='ios'?40:20 },
  photoCardHandle:   { width:40, height:4, borderRadius:2, backgroundColor:'#E2E8F0', alignSelf:'center', marginBottom:16 },
  photoCardTitle:    { fontSize:18, fontWeight:'800', color:'#0F172A', marginBottom:4 },
  photoCardSub:      { fontSize:12, color:'#94A3B8', marginBottom:20 },
  photoOptionRow:    { flexDirection:'row', alignItems:'center', paddingVertical:14, borderBottomWidth:1, borderBottomColor:'#F1F5F9', gap:12 },
  photoOptionIcon:   { width:40, height:40, borderRadius:12, justifyContent:'center', alignItems:'center' },
  photoOptionText:   { flex:1, fontSize:15, fontWeight:'600', color:'#0F172A' },
  photoCardCancel:   { marginTop:16, backgroundColor:'#F1F5F9', borderRadius:14, paddingVertical:14, alignItems:'center' },
  photoCardCancelText:{ fontSize:15, fontWeight:'700', color:'#374151' },
});